import React, { useEffect, useRef, useState } from "react";
import {
    Chart as ChartJS,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from "chart.js";
import { ChartLine, Minus, TrendingDown, TrendingUp, X } from "lucide-react";
import { Line } from "react-chartjs-2";
import "./StockScanner.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";
import useSectorStrategyExecutionWebsocket from "service/websocket/SectorStrategyExecutionWebsocket";
import { useAuth } from "component/common/AuthContext";
import { getBrokerAccounts, getBrokerId } from "service/api/BrokerAccountApi";
import {
    getSectorStrategies,
    getSectorStrategyById,
    getSectorStrategyExecutionCapabilities,
    getSectorStrategyEntryCalculations,
    getSectorStrategyCandidates,
    getSectorStrategyExecutionHistory,
    getSectorStrategyExecutionHistoryForRange,
    getSectorStrategyTrades,
    saveSectorStrategy,
    squareOffSectorStrategy,
    squareOffSectorStrategyTrade,
    updateSectorStrategy,
} from "service/api/SectorStrategyApi";

ChartJS.register(Filler, LinearScale, LineElement, PointElement, Tooltip);

const STOCK_PRE_OPEN_CHART_DATA_URL = `${environment.stockApiBaseUrl}/stock-pre-open-data/chart-data`;
const PRE_OPEN_CHART_LINE_COLOR = "#ff9900";
const PRE_OPEN_CHART_FILL_COLOR = "rgba(255, 153, 0, 0.24)";
const SUPPORT_LINE_COLOR = "#16a34a";
const RESISTANCE_LINE_COLOR = "#e11d48";
const EMA200_BLINK_MAX_PERCENT = 0.5;
const ENTRY_CONDITION_OPTIONS = [
    { level: "EMA200", label: "EMA 200", source: "Stock LTP vs EMA200" },
    { level: "5MH", label: "First 5-Min High", source: "Stock LTP" },
    { level: "5ML", label: "First 5-Min Low", source: "Stock LTP" },
    { level: "15MH", label: "First 15-Min High", source: "Stock LTP" },
    { level: "15ML", label: "First 15-Min Low", source: "Stock LTP" },
    { level: "WH", label: "Option Week High", source: "Option premium" },
    { level: "WL", label: "Option Week Low", source: "Option premium" },
];
const DEFAULT_ENTRY_CONDITIONS = ENTRY_CONDITION_OPTIONS.map(({ level }) => ({
    level,
    enabled: level === "WH",
    slippagePercent: "1.5",
}));
const DEFAULT_BUY_SELL_EXECUTION_SETTINGS = {
    tradingMode: "PAPER",
    productType: "INTRADAY",
    orderType: "MARKET",
    defaultLots: "1",
    startTime: "09:20",
    endTime: "15:10",
    squareOffTime: "15:20",
    stopLoss: "2000",
    target: "2500",
    maximumDailyLoss: "",
    maximumTradesPerDay: "5",
    maximumOpenPositions: "3",
    topGainerLoserCount: "20",
    maximumOrderValue: "",
    maximumSlippagePercent: "1",
    entryLevel: "WH",
    maximumEntryDistancePercent: "1.5",
    maximumIntradayRangePercent: "1.5",
    fiveMinuteRangeEnabled: true,
    fifteenMinuteRangeEnabled: true,
    entryConditions: DEFAULT_ENTRY_CONDITIONS,
    allowBuy: true,
    allowSell: true,
    oneTradePerSymbolPerDay: true,
};
const toNumber = (value) => {
    const parsedValue = Number.parseFloat(
        String(value ?? "")
            .replace(/,/g, "")
            .replace("%", "")
    );

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatNumber = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) {
        return value ?? "--";
    }

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const getTradeInvestmentAmount = (trade) => {
    const entryPrice = toNumber(trade?.entryPrice);
    const quantity = toNumber(trade?.quantity);
    if (entryPrice == null || quantity == null || quantity <= 0) return 0;
    return entryPrice * quantity;
};

// Target and stop-loss are persisted on the execution record. Keep the small
// fallbacks for older records that used the shorter field names so the table
// remains readable while records are migrated.
const getTradeTarget = (trade) => trade?.targetPrice ?? trade?.target;
const getTradeStopLoss = (trade) => trade?.stopLossPrice ?? trade?.stopLoss;

const formatSignedPercent = (value) => {
    const numericValue = toNumber(value);
    if (numericValue == null) return "--";
    return `${numericValue > 0 ? "+" : ""}${formatNumber(numericValue)}%`;
};

const getEma200LtpMetrics = (row) => {
    const ltp = toNumber(row?.lp);
    const ema200 = toNumber(row?.ema200);
    const backendDifference = toNumber(row?.ema200LtpDifference);
    const backendDifferencePercent = toNumber(row?.ema200LtpDifferencePercent);

    if (ltp == null || ema200 == null || ema200 <= 0) {
        return {
            difference: backendDifference,
            differencePercent: backendDifferencePercent,
        };
    }

    const difference = backendDifference ?? ltp - ema200;
    const differencePercent = backendDifferencePercent ?? (difference / ema200) * 100;
    return { difference, differencePercent };
};

const formatEma200LtpMetrics = ({ difference, differencePercent }) => {
    const differenceText = difference == null ? "--" : formatNumber(difference);
    const percentageText = differencePercent == null
        ? "--"
        : `${formatNumber(differencePercent)}%`;
    const percentageValue = toNumber(differencePercent);
    return {
        differenceText,
        percentageText,
        shouldBlink: percentageValue != null && Math.abs(percentageValue) > 0
            && Math.abs(percentageValue) <= EMA200_BLINK_MAX_PERCENT,
    };
};

const SYMBOL_SERIES_SUFFIX_SET = new Set(["EQ", "BE", "BZ", "BL", "SM", "ST"]);

const normalizeSymbol = (value) => {
    const rawValue = String(value ?? "").trim().toUpperCase();

    if (!rawValue) {
        return "";
    }

    const exchangeTrimmedValue = rawValue.includes(":")
        ? rawValue.split(":").pop()?.trim() ?? ""
        : rawValue;
    const symbolParts = exchangeTrimmedValue.split("-");

    if (symbolParts.length > 1) {
        const lastPart = symbolParts[symbolParts.length - 1];

        if (SYMBOL_SERIES_SUFFIX_SET.has(lastPart)) {
            return symbolParts.slice(0, -1).join("-");
        }
    }

    return exchangeTrimmedValue;
};

const executionTimestamp = (trade) => {
    const timestamp = trade?.createdAt ?? trade?.entryAt ?? trade?.entryRequestedAt ?? (
        trade?.createdDate && trade?.createdTime
            ? `${trade.createdDate} ${trade.createdTime}`
            : null
    );
    if (!timestamp) return null;

    const rawTimestamp = String(timestamp).trim();
    const parsedTimestamp = Date.parse(rawTimestamp);
    if (Number.isFinite(parsedTimestamp)) return parsedTimestamp;

    // Older records use the market-local `yyyy-MM-dd HH:mm:ss` form. Add the
    // market offset only when the runtime cannot parse that representation.
    const marketTimestamp = Date.parse(
        `${rawTimestamp.replace(" ", "T")}+05:30`
    );
    return Number.isFinite(marketTimestamp) ? marketTimestamp : null;
};

const compareExecutionTrades = (leftTrade, rightTrade) => {
    const leftTimestamp = executionTimestamp(leftTrade);
    const rightTimestamp = executionTimestamp(rightTrade);

    if (leftTimestamp != null || rightTimestamp != null) {
        if (leftTimestamp == null) return 1;
        if (rightTimestamp == null) return -1;
        if (leftTimestamp !== rightTimestamp) return rightTimestamp - leftTimestamp;
    }

    // A timestamp collision is possible when legacy records only have
    // second-level precision. The immutable id keeps the order deterministic.
    const leftKey = String(leftTrade?.id ?? leftTrade?.signalKey ?? "");
    const rightKey = String(rightTrade?.id ?? rightTrade?.signalKey ?? "");
    return rightKey.localeCompare(leftKey, undefined, { numeric: true });
};

const sortExecutionTrades = (trades) => (
    Array.isArray(trades) ? [...trades].sort(compareExecutionTrades) : []
);

const mergeExecutionTrade = (trades, incomingTrade) => {
    if (!incomingTrade?.id) return sortExecutionTrades(trades);

    const currentTrades = Array.isArray(trades) ? trades : [];
    const existingIndex = currentTrades.findIndex(
        (trade) => trade?.id === incomingTrade.id
    );

    if (existingIndex >= 0) {
        // Replace the payload in place. Live LTP/SL updates must not move the
        // row to the top of the table on every websocket message.
        const nextTrades = [...currentTrades];
        nextTrades[existingIndex] = {
            ...currentTrades[existingIndex],
            ...incomingTrade,
        };
        return nextTrades;
    }

    return sortExecutionTrades([...currentTrades, incomingTrade]);
};

const normalizeScannerKey = (value) => String(value ?? "").trim().toUpperCase();
const isValidTime24 = (value) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(String(value ?? ""));
const timeToMinutes = (value) => {
    if (!isValidTime24(value)) return null;
    const [hours, minutes] = String(value).split(":").map(Number);
    return (hours * 60) + minutes;
};
const createTimeOptions = (startTime, endTime, stepMinutes = 5) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    if (startMinutes == null || endMinutes == null || startMinutes > endMinutes) return [];

    const options = [];
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += stepMinutes) {
        const hours = String(Math.floor(minutes / 60)).padStart(2, "0");
        const minutePart = String(minutes % 60).padStart(2, "0");
        options.push(`${hours}:${minutePart}`);
    }
    return options;
};
const EXECUTION_ENTRY_TIME_OPTIONS = createTimeOptions("09:15", "15:30");
const isBrokerConnected = (status = "") =>
    ["SUCCESS", "CONNECTED", "ACTIVE", "VERIFIED"].includes(
        String(status).toUpperCase().replace(/[\s_-]/g, "")
    );
const isBrokerReady = (broker) => broker?.active !== false &&
    isBrokerConnected(broker?.status);
const toExecutionSettingString = (value, fallback = "") =>
    value == null ? fallback : String(value);
const getApiErrorMessage = (error, fallback) =>
    error?.response?.data?.message ?? error?.message ?? fallback;
const isBrokerFlatConfirmationPending = (trade) =>
    trade?.status === "OPEN" &&
    trade?.brokerExitStatus === "FLAT_CONFIRMATION_PENDING";
const isExternallyClosedTrade = (trade) =>
    trade?.status === "CLOSED" &&
    (trade?.exitReason === "EXTERNAL_BROKER_EXIT" ||
        trade?.brokerExitStatus === "CLOSED_EXTERNALLY");
const getExecutionStatusLabel = (trade) => {
    if (isBrokerFlatConfirmationPending(trade)) return "VERIFYING BROKER";
    if (isExternallyClosedTrade(trade)) return "CLOSED FROM BROKER";
    return trade?.status ?? "--";
};
const getExecutionStatusClass = (trade) => isBrokerFlatConfirmationPending(trade)
    ? "verifying_broker"
    : isExternallyClosedTrade(trade)
        ? "closed_externally"
        : String(trade?.status ?? "unknown").toLowerCase();
const formatExecutionDateTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(date);
};
const getMarketDateString = () => {
    const parts = new Intl.DateTimeFormat("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date()).reduce((result, part) => {
        if (part.type !== "literal") result[part.type] = part.value;
        return result;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
};
const entryConditionsFromApi = (settings = {}, fallbackSettings = {}) => {
    const apiConditions = Array.isArray(settings.entryConditions)
        ? settings.entryConditions
        : [];
    const fallbackConditions = Array.isArray(fallbackSettings.entryConditions)
        ? fallbackSettings.entryConditions
        : [];
    const sourceConditions = apiConditions.length > 0
        ? apiConditions
        : fallbackConditions;
    const conditionByLevel = new Map(sourceConditions.map((condition) => [
        String(condition?.level ?? "").trim().toUpperCase(),
        condition,
    ]));
    const legacyLevel = String(
        settings.entryLevel ?? fallbackSettings.entryLevel ?? "WH"
    ).trim().toUpperCase();
    const legacySlippage = toExecutionSettingString(
        settings.maximumEntryDistancePercent ??
            fallbackSettings.maximumEntryDistancePercent,
        "1.5"
    );

    return ENTRY_CONDITION_OPTIONS.map(({ level }) => {
        const condition = conditionByLevel.get(level);
        return {
            level,
            enabled: condition
                ? Boolean(condition.enabled)
                : sourceConditions.length === 0 && level === legacyLevel,
            slippagePercent: toExecutionSettingString(
                condition?.slippagePercent,
                legacySlippage
            ),
        };
    });
};
const executionSettingsFromApi = (settings = {}, fallbackSettings = {}) => {
    const mergedSettings = {
        ...DEFAULT_BUY_SELL_EXECUTION_SETTINGS,
        ...fallbackSettings,
        ...settings,
    };
    return {
        ...mergedSettings,
        defaultLots: toExecutionSettingString(mergedSettings.defaultLots, "1"),
        stopLoss: toExecutionSettingString(mergedSettings.stopLoss),
        target: toExecutionSettingString(mergedSettings.target),
        maximumDailyLoss: toExecutionSettingString(mergedSettings.maximumDailyLoss),
        maximumTradesPerDay: toExecutionSettingString(
            mergedSettings.maximumTradesPerDay,
            "5"
        ),
        maximumOpenPositions: toExecutionSettingString(
            mergedSettings.maximumOpenPositions,
            "3"
        ),
        topGainerLoserCount: toExecutionSettingString(
            mergedSettings.topGainerLoserCount,
            "20"
        ),
        maximumOrderValue: toExecutionSettingString(mergedSettings.maximumOrderValue),
        maximumSlippagePercent: toExecutionSettingString(
            mergedSettings.maximumSlippagePercent,
            "1"
        ),
        entryLevel: String(mergedSettings.entryLevel ?? "WH").trim().toUpperCase(),
        maximumEntryDistancePercent: toExecutionSettingString(
            mergedSettings.maximumEntryDistancePercent,
            "1.5"
        ),
        maximumIntradayRangePercent: toExecutionSettingString(
            mergedSettings.maximumIntradayRangePercent,
            "1.5"
        ),
        fiveMinuteRangeEnabled: mergedSettings.fiveMinuteRangeEnabled == null
            ? true
            : Boolean(mergedSettings.fiveMinuteRangeEnabled),
        fifteenMinuteRangeEnabled: mergedSettings.fifteenMinuteRangeEnabled == null
            ? true
            : Boolean(mergedSettings.fifteenMinuteRangeEnabled),
        entryConditions: entryConditionsFromApi(settings, fallbackSettings),
    };
};

const selectMostRecentlySavedBuySellStrategy = (strategies) =>
    (Array.isArray(strategies) ? strategies : [])
        .filter((item) => item?.strategyType === "SECTOR_STRATEGY_BUY_SELL")
        .reduce((latest, candidate) => {
            if (!latest) return candidate;
            const latestTime = Date.parse(
                latest.updatedAt ?? latest.savedAt ?? latest.createdAt ?? 0
            ) || 0;
            const candidateTime = Date.parse(
                candidate.updatedAt ?? candidate.savedAt ?? candidate.createdAt ?? 0
            ) || 0;
            return candidateTime > latestTime ? candidate : latest;
        }, null);
const formatSectorDisplayName = (value) => {
    const sector = String(value ?? "").trim()
        .replace(/^NIFTY(?:\s+|(?=[A-Z]))/i, "")
        .trim();
    return sector || "--";
};

const MID_CROSS_BUY = "MID_CROSS_BUY";
const MID_CROSS_SELL = "MID_CROSS_SELL";

const MID_CROSS_BUY_TAB = "MID_CROSS_BUY_TAB";
const MID_CROSS_SELL_TAB = "MID_CROSS_SELL_TAB";
const MC_FILTER_TAB = "MC_FILTER_TAB";
const EXECUTION_RISK_TAB = "EXECUTION_RISK_TAB";
const TOP_20_GL_TAB = "TOP_20_GL_TAB";
const BUY_SELL_TAB = "BUY_SELL_TAB";
const POD_TAB = "POD_TAB";
const SECTOR_STRATEGIES_TAB = "SECTOR_STRATEGIES_TAB";
const VAH_VAL_TAB = "VAH_VAL_TAB";
const ALL_STOCK_DATA_TAB = "ALL_STOCK_DATA_TAB";
const RSI_BAND_SCAN_TAB = "RSI_BAND_SCAN_TAB";
const RSI_BAND_BULLISH = "RSI_BAND_BULLISH";
const RSI_BAND_BEARISH = "RSI_BAND_BEARISH";
const MC_BUY_FILTER_TAB = "MC_BUY_FILTER_TAB";
const MC_SELL_FILTER_TAB = "MC_SELL_FILTER_TAB";

const SCANNER_SECTION_TITLE_MAP = {
    [MID_CROSS_BUY]: "MID CROSS BUY",
    [MID_CROSS_SELL]: "MID CROSS SELL",
    [MC_FILTER_TAB]: "MC FILTER",
    [EXECUTION_RISK_TAB]: "EXECUTION & RISK",
    [TOP_20_GL_TAB]: "TOP (20) G/L",
    [BUY_SELL_TAB]: "BUY/SELL",
    [POD_TAB]: "POD",
    [SECTOR_STRATEGIES_TAB]: "SECTOR STRATEGIES",
    [VAH_VAL_TAB]: "VAH/VAL",
    [ALL_STOCK_DATA_TAB]: "ALL STOCK DATA",
    [RSI_BAND_SCAN_TAB]: "RSI_BAND_SCAN",
    [RSI_BAND_BULLISH]: "BULLISH",
    [RSI_BAND_BEARISH]: "BEARISH",
    [MC_BUY_FILTER_TAB]: "MC BUY FILTER",
    [MC_SELL_FILTER_TAB]: "MC SELL FILTER",
};

const STRONG_SIGNAL_OPTIONS = [
    "STRNG BUY",
    "STRNG SELL",
    "WEEK BUY",
    "WEEK SELL",
    "NO SIGNAL",
];

const getPreOpenBuyQuantity = (row) =>
    row?.preOpenMarket?.totalBuyQuantity ??
    row?.preOpenMarket?.totalBuyQty ??
    row?.buyQty ??
    row?.buyQuantity;

const getPreOpenSellQuantity = (row) =>
    row?.preOpenMarket?.totalSellQuantity ??
    row?.preOpenMarket?.totalSellQty ??
    row?.sellQty ??
    row?.sellQuantity;

const getPodSignal = (row) => {
    const buyQuantityValue = toNumber(getPreOpenBuyQuantity(row)) ?? 0;
    const sellQuantityValue = toNumber(getPreOpenSellQuantity(row)) ?? 0;

    if (buyQuantityValue > sellQuantityValue) return "BUY";
    if (buyQuantityValue < sellQuantityValue) return "SELL";
    return "NO SIGNAL";
};

const getBreakoutSignal = (row) => {
    const ltpValue = toNumber(row?.ltp);
    const firstFiveMinuteHighValue = toNumber(row?.firstFiveMinuteHigh);
    const firstFiveMinuteLowValue = toNumber(row?.firstFiveMinuteLow);

    if (
        ltpValue != null &&
        firstFiveMinuteHighValue != null &&
        ltpValue > firstFiveMinuteHighValue
    ) {
        return "BUY";
    }

    if (
        ltpValue != null &&
        firstFiveMinuteLowValue != null &&
        ltpValue < firstFiveMinuteLowValue
    ) {
        return "SELL";
    }

    return "NO SIGNAL";
};

const getStrongSignal = (row) => {
    const ltpValue = toNumber(row?.ltp);
    const finalPriceValue = toNumber(row?.finalPrice);
    const previousCloseValue = toNumber(row?.previousClose);
    const podSignal = getPodSignal(row);
    const breakoutSignal = getBreakoutSignal(row);
    const isBullishPriceSequence =
        ltpValue != null &&
        finalPriceValue != null &&
        previousCloseValue != null &&
        ltpValue > finalPriceValue &&
        ltpValue > previousCloseValue;
    const isBearishPriceSequence =
        ltpValue != null &&
        finalPriceValue != null &&
        previousCloseValue != null &&
        ltpValue < finalPriceValue &&
        ltpValue < previousCloseValue;

    if (podSignal === "BUY" && isBullishPriceSequence && breakoutSignal === "BUY") {
        return "STRNG BUY";
    }

    if (podSignal === "SELL" && isBearishPriceSequence && breakoutSignal === "SELL") {
        return "STRNG SELL";
    }

    if (podSignal === "SELL" && isBullishPriceSequence && breakoutSignal === "BUY") {
        return "WEEK BUY";
    }

    if (podSignal === "BUY" && isBearishPriceSequence && breakoutSignal === "SELL") {
        return "WEEK SELL";
    }

    return "NO SIGNAL";
};

const getPodSignalClassName = (signal) => {
    if (String(signal ?? "").endsWith("BUY")) return "pod-signal-buy";
    if (String(signal ?? "").endsWith("SELL")) return "pod-signal-sell";
    return "pod-signal-neutral";
};

const getStockPreOpenAduCount = (payload) => {
    const directAduCount = payload?.aduCount ??
        payload?.data?.aduCount ??
        payload?.response?.data?.aduCount;

    if (directAduCount) {
        return directAduCount;
    }

    if (Array.isArray(payload)) {
        return payload.find((item) => item?.aduCount)?.aduCount ?? {};
    }

    if (Array.isArray(payload?.data)) {
        return payload.data.find((item) => item?.aduCount)?.aduCount ?? {};
    }

    return {};
};

const formatAduCountValue = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) return "--";

    return numericValue.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    });
};

const createSectorDetailsMap = (
    sectorData = [],
    isSectorMatch = () => false,
    restrictToSelectedStocks = true
) =>
    sectorData.reduce((accumulator, sector) => {
        const sectorPercent = toNumber(sector?.pChange);

        if (sectorPercent == null || !isSectorMatch(sectorPercent)) {
            return accumulator;
        }

        (sector?.sectorialIndicesStockModelList ?? []).forEach((stock) => {
            const symbol = normalizeSymbol(stock?.symbol);

            if (
                !symbol ||
                (restrictToSelectedStocks && !SECTOR_SELECTED_STOCK_SET.has(symbol)) ||
                accumulator.has(symbol)
            ) {
                return;
            }

            accumulator.set(symbol, {
                sectorName: sector?.index ?? "--",
                sectorPercent,
            });
        });

        return accumulator;
    }, new Map());

const createLotSizeBySymbolMap = (stockDayRangeData = []) =>
    stockDayRangeData.reduce((accumulator, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker
        );
        const lotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot;

        if (!symbol || lotSize == null || accumulator.has(symbol)) {
            return accumulator;
        }

        accumulator.set(symbol, lotSize);
        return accumulator;
    }, new Map());

const attachSectorDetailsToStocks = (
    stocks = [],
    sectorDetailsBySymbol = new Map(),
    requireSectorDetails = true
) =>
    stocks
        .map((stock) => {
            const sectorDetails = sectorDetailsBySymbol.get(normalizeSymbol(stock.symbol));

            if (!sectorDetails && requireSectorDetails) {
                return null;
            }

            return {
                ...stock,
                sectorName: sectorDetails?.sectorName ?? stock?.sectorName ?? null,
                sectorPercent: sectorDetails?.sectorPercent ?? stock?.sectorPercent ?? null,
            };
        })
        .filter(Boolean);

const getScannerRowsByKey = (scannerDataSource, key) => {
    const normalizedKey = normalizeScannerKey(key);

    if (scannerDataSource instanceof Map) {
        if (Array.isArray(scannerDataSource.get(key))) {
            return scannerDataSource.get(key);
        }

        for (const [entryKey, entryValue] of scannerDataSource.entries()) {
            if (normalizeScannerKey(entryKey) === normalizedKey) {
                return Array.isArray(entryValue) ? entryValue : [];
            }
        }

        return [];
    }

    if (scannerDataSource && typeof scannerDataSource === "object" && !Array.isArray(scannerDataSource)) {
        if (Array.isArray(scannerDataSource[key])) {
            return scannerDataSource[key];
        }

        const matchedObjectKey = Object.keys(scannerDataSource).find(
            (entryKey) => normalizeScannerKey(entryKey) === normalizedKey
        );

        return matchedObjectKey && Array.isArray(scannerDataSource[matchedObjectKey])
            ? scannerDataSource[matchedObjectKey]
            : [];
    }

    return [];
};

const hasScannerEntries = (scannerDataSource) => {
    if (scannerDataSource instanceof Map) {
        return scannerDataSource.size > 0;
    }

    return Boolean(
        scannerDataSource &&
        typeof scannerDataSource === "object" &&
        !Array.isArray(scannerDataSource) &&
        Object.keys(scannerDataSource).length > 0
    );
};

const getRsiBandRows = (payload, side) => {
    const candidates = [
        payload,
        payload?.data,
        payload?.response,
        payload?.response?.data,
    ];
    const normalizedSide = normalizeScannerKey(side);

    for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
            continue;
        }

        const matchingKey = Object.keys(candidate).find(
            (key) => normalizeScannerKey(key) === normalizedSide
        );

        if (matchingKey && Array.isArray(candidate[matchingKey])) {
            return candidate[matchingKey];
        }
    }

    return [];
};

const getFoTopRowsByKey = (foTopGainerLooserData, key) =>
    Array.isArray(foTopGainerLooserData?.[key]?.data)
        ? foTopGainerLooserData[key].data
        : [];

const createSymbolSet = (rows = []) =>
    new Set(
        rows
            .map((row) =>
                normalizeSymbol(
                    row?.symbol ??
                    row?.stockSymbol ??
                    row?.tradingSymbol ??
                    row?.ticker
                )
            )
            .filter(Boolean)
    );

const isMediaSector = (sector) =>
    String(sector?.index ?? sector?.sectorName ?? sector?.name ?? "")
        .split(":")
        .pop()
        ?.trim()
        .toUpperCase()
        .replace(/^NIFTY\s+/, "") === "MEDIA";

const getRankedSectors = (sectorData = []) =>
    sectorData
        .filter((sector) => !isMediaSector(sector))
        .map((sector) => ({
            sector,
            sectorPercent: toNumber(sector?.pChange),
        }))
        .filter((entry) => entry.sectorPercent != null)
        .sort((firstEntry, secondEntry) =>
            firstEntry.sectorPercent - secondEntry.sectorPercent
        );

const createSectorSymbolSet = (sectors = []) =>
    new Set(
        sectors
            .flatMap((sector) => sector?.sectorialIndicesStockModelList ?? [])
            .map((stock) => normalizeSymbol(stock?.symbol))
            .filter(Boolean)
    );

const createSectorNameSet = (sectors = []) =>
    new Set(
        sectors
            .map((sector) => String(sector?.index ?? "").trim().toUpperCase())
            .filter(Boolean)
    );

const filterStocksByFoTopAndSectors = (
    stocks = [],
    foTopSymbolSet = new Set(),
    sectorSymbolSet = new Set(),
    sectorNameSet = new Set()
) =>
    stocks.filter((stock) => {
        const symbol = normalizeSymbol(stock?.symbol);
        const sectorName = String(stock?.sectorName ?? "").trim().toUpperCase();

        return (
            symbol &&
            foTopSymbolSet.has(symbol) &&
            (sectorSymbolSet.has(symbol) || sectorNameSet.has(sectorName))
        );
    });

const getStockPreOpenRows = (payload) => {
    const candidates = [
        payload?.data,
        payload?.response?.data,
        payload?.data?.data,
        payload,
    ];

    return candidates.find(Array.isArray) ?? [];
};

const normalizeStockPreOpenRows = (payload) =>
    getStockPreOpenRows(payload).map((entry, index) => {
        const metadata = entry?.metadata ?? entry ?? {};
        const detail = entry?.detail ?? {};
        const preOpenMarket = detail?.preOpenMarket ?? entry?.preOpenMarket ?? {};
        const totalTurnover = toNumber(
            metadata?.totalTurnover ?? detail?.totalTurnover ?? entry?.totalTurnover
        );

        return {
            symbol: normalizeSymbol(metadata?.symbol ?? entry?.symbol) || "--",
            chartIndex:
                metadata?.identifier ?? entry?.identifier ??
                `${normalizeSymbol(metadata?.symbol ?? entry?.symbol)}EQN`,
            previousClose:
                detail?.previousClose ?? metadata?.previousClose ?? entry?.previousClose,
            support:
                entry?.support ?? metadata?.support ?? detail?.support,
            resistance:
                entry?.resistance ?? metadata?.resistance ?? detail?.resistance,
            iep: metadata?.iep ?? preOpenMarket?.IEP ?? preOpenMarket?.iep ?? entry?.iep,
            change: detail?.change ?? metadata?.change ?? entry?.change,
            pChange:
                detail?.pChange ?? metadata?.pChange ?? entry?.pChange ?? entry?.percentChange,
            finalPrice:
                preOpenMarket?.finalPrice ?? metadata?.finalPrice ?? detail?.lastPrice ??
                metadata?.lastPrice ?? entry?.finalPrice,
            finalQuantity:
                preOpenMarket?.finalQuantity ?? metadata?.finalQuantity ?? entry?.finalQuantity ??
                preOpenMarket?.totalTradedVolume,
            avgInOI:
                entry?.avgInOI ?? entry?.avgInOi ?? entry?.averageInOI ?? entry?.averageInOi ??
                metadata?.avgInOI ?? metadata?.avgInOi ?? detail?.avgInOI ?? detail?.avgInOi,
            chp:
                entry?.chp ?? entry?.liveP ?? entry?.livePercent ??
                metadata?.chp ?? metadata?.liveP ?? detail?.chp ?? detail?.liveP,
            valueCrores:
                metadata?.valueCrores ?? entry?.valueCrores ??
                (totalTurnover == null ? null : totalTurnover / 10000000),
            preOpenMarket,
            key: `${metadata?.symbol ?? entry?.symbol ?? "pod"}-${index}`,
        };
    });

const formatPreOpenNumber = (value, maximumFractionDigits = 2) => {
    const numericValue = toNumber(value);

    if (numericValue == null) return "--";

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits,
    });
};

const formatPreOpenPrice = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) return "-";

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatPreOpenQuantity = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null || numericValue === 0) return "-";

    return numericValue.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    });
};

const createStockAnalysisBySymbolMap = (stockRows = []) =>
    (Array.isArray(stockRows) ? stockRows : []).reduce((stockAnalysisBySymbol, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ?? stock?.stockSymbol ?? stock?.tradingSymbol ?? stock?.ticker
        );
        const ltp = stock?.lp ?? stock?.ltp ?? stock?.lastPrice;
        const firstFiveMinuteHigh =
            stock?.firstFiveMinuteHigh ?? stock?.first5MinuteHigh;
        const firstFiveMinuteLow =
            stock?.firstFiveMinuteLow ?? stock?.first5MinuteLow;
        const avgInOI =
            stock?.avgInOI ?? stock?.avgInOi ?? stock?.averageInOI ?? stock?.averageInOi;
        const chp =
            stock?.chp ?? stock?.liveP ?? stock?.livePercent ?? stock?.changePercent ??
            stock?.pChange ?? stock?.percentChange;
        const support = stock?.support;
        const resistance = stock?.resistance;

        if (symbol) {
            stockAnalysisBySymbol.set(symbol, {
                ltp,
                firstFiveMinuteHigh,
                firstFiveMinuteLow,
                avgInOI,
                chp,
                support,
                resistance,
            });
        }

        return stockAnalysisBySymbol;
    }, new Map());

const getPreOpenChartPriceRange = (graphData = []) =>
    graphData.reduce((priceRange, point) => {
        const price = Number(point?.[1]);

        if (!Number.isFinite(price) || price === 0) return priceRange;

        return {
            highest: priceRange.highest == null
                ? price
                : Math.max(priceRange.highest, price),
            lowest: priceRange.lowest == null
                ? price
                : Math.min(priceRange.lowest, price),
        };
    }, { highest: null, lowest: null });

const normalizeScannerStocks = (stockScannerDataList = [], lotSizeBySymbol = new Map()) =>
    stockScannerDataList.map((stock, index) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker ??
            "--"
        ) || "--";
        const fnoLotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot ??
            lotSizeBySymbol.get(symbol) ??
            "--";

        return {
            symbolCount: stock?.symbolCount ?? stock?.symbol_count ?? stock?.symbolCnt ?? stock?.count ?? "--",
            symbol,
            status: String(stock?.status ?? "").trim().toUpperCase(),
            changePercent: toNumber(stock?.["%_change"] ?? stock?.changePercent),
            createdDate: stock?.createdDate ?? "--",
            createdTime: stock?.createdTime ?? "--",
            volume: stock?.volume,
            triggerDate: stock?.triggerDate ?? "--",
            triggerTime: stock?.triggerTime ?? "--",
            ltp: stock?.ltp ?? stock?.close,
            fnoLotSize,
            __scannerKey: `${stock?.symbol ?? stock?.stockSymbol ?? "stock"}-${index}`,
        };
    });

    const SECTOR_SELECTED_STOCK_SET = new Set([
        "UNOMINDA", "BAJAJ-AUTO", "TIINDIA", "BOSCHLTD", "SONACOMS", "TVSMOTOR", "EICHERMOT", "HEROMOTOCO", "M&M", "BHARATFORG",
        "FORCEMOTOR", "PFC", "RECLTD", "BAJAJFINSV", "MFSL", "LICHSGFIN", "JIOFIN", "CHOLAIFIN", "SHRIRAMFIN", "MUTHOOTFIN",
        "BSE", "SBIN", "BAJAJFINANCE", "DABUR", "TATACONSUM", "HINDUNILVR", "GODREJCP", "UNITEDSPR", "NESTLEIND", "ITC",
        "VBL", "UBL", "BRITANNIA", "COFORGE", "PERSISTENT", "LTM", "INFY", "HCLTECH", "TECHMM", "MPHASIS", "TCS", "OFSS",
        "NATIONALUM", "HINDCOPPER", "HINDZINC", "ADANIENT", "JSWSTEEL", "JINDALSTEL", "HINDALCO", "APLAPOLLO", "TATASTEEL",
        "VEDL", "MANKIND", "LAURUSLABS", "SUNPHARMA", "TORNTPHARM", "AUROPHARMA", "DIVISLAB", "GLENMARK", "ALKEM", "BIOCON",
        "DRREDDY", "CIPLA", "LUPIN", "OBEROIRLTY", "PRESTIGE", "GODREJPROP", "LODHA", "DLF", "TITAN", "AMBER", "HAVELLS",
        "DIXON", "VOLTAS", "BLUESTARCO", "RELIANCE", "OIL", "ATGL", "ONGC", "BPCL", "PIDILITIND", "SRF", "PIIND", "CGPOWER",
        "NTPC", "TORNTPOWER", "JSWENERGY", "TATAPOWER", "SIEMENS", "ADANIGREEN", "ADANIENSOL", "ABB", "ADANIPOWER",
        "COALINDIA", "POWERINDIA", "HAL", "BEL", "BDL", "BHEL", "MAZDOCK", "COCHINSHIP", "DALBHARAT", "POLYCAB",
        "PNBHOUSING", "DMART", "360ONE", "ETERNAL", "ASIANPAINT", "LT", "HDFCBANK", "BANDHANBNK", "INDUSINDBK", "TECHM",
        "KOTAKBANK", "ICICIBANK", "AXISBANK"
    ]);

export default function StockScanner() {
    const { user } = useAuth();
    const { latestTrade: latestExecutionTrade, isConnected: executionSocketConnected } =
        useSectorStrategyExecutionWebsocket(user?.accessToken);
    const stockScannerRef = useRef(null);
    const stickyTopShellRef = useRef(null);
    const strongSignalFilterRef = useRef(null);
    const { options, sectorialIndicesData, foTopGainerLooserData,
        atlasScannerData, stockPreOpenData, stockAnalysisData, sectorRsiBandScannerData,
        sectorStrategiesData
    } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl,
        [
            "options",
            "sectorialIndicesData",
            "foTopGainerLooserData",
            "atlasScannerData",
            "stockPreOpenData",
            "stockAnalysisData",
            "sectorRsiBandScannerData",
            "sectorStrategiesData",
        ]
    );
    const allStockDataRows = Array.isArray(sectorStrategiesData?.ALL_STOCK_DATA)
        ? sectorStrategiesData.ALL_STOCK_DATA
        : [];

    const [sectorData, setSectorData] = useState([]);
    const [midCrossScannerData, setMidCrossScannerData] = useState({});
    const [activeTab, setActiveTab] = useState(BUY_SELL_TAB);
    const [entryHistoryPopup, setEntryHistoryPopup] = useState(null);
    const [rankHistoryPopup, setRankHistoryPopup] = useState(null);
    const [buySellBrokers, setBuySellBrokers] = useState([]);
    const [selectedBuySellBrokerId, setSelectedBuySellBrokerId] = useState("");
    const [buySellBrokerLoading, setBuySellBrokerLoading] = useState(true);
    const [buySellBrokerLoadError, setBuySellBrokerLoadError] = useState(false);
    const [buySellStrategyName, setBuySellStrategyName] = useState("Automatic Sector Strategy");
    const [buySellStrategySaved, setBuySellStrategySaved] = useState(false);
    const [buySellStrategySaveError, setBuySellStrategySaveError] = useState("");
    const [buySellSettingsTab, setBuySellSettingsTab] = useState("EXECUTION_RISK");
    const [buySellStrategyStatus, setBuySellStrategyStatus] = useState("DRAFT");
    const [buySellActivatedTradingMode, setBuySellActivatedTradingMode] = useState("");
    const [buySellStrategyMessage, setBuySellStrategyMessage] = useState("");
    const [buySellStrategyId, setBuySellStrategyId] = useState("");
    const [buySellStrategySaving, setBuySellStrategySaving] = useState(false);
    const [buySellTrades, setBuySellTrades] = useState([]);
    const [buySellEntryCalculations, setBuySellEntryCalculations] = useState([]);
    const [buySellCandidateRows, setBuySellCandidateRows] = useState({});
    const vahBreakoutRows = Array.isArray(buySellCandidateRows.VAH_BREAKOUT_STOCK)
        ? buySellCandidateRows.VAH_BREAKOUT_STOCK : [];
    const valBreakdownRows = Array.isArray(buySellCandidateRows.VAL_BREAKDOWN_STOCK)
        ? buySellCandidateRows.VAL_BREAKDOWN_STOCK : [];
    const [buySellTradesLoading, setBuySellTradesLoading] = useState(false);
    const [executionHistoryStartDate, setExecutionHistoryStartDate] = useState(
        getMarketDateString
    );
    const [executionHistoryEndDate, setExecutionHistoryEndDate] = useState(
        getMarketDateString
    );
    const [executionHistoryTrades, setExecutionHistoryTrades] = useState([]);
    const [executionHistoryLoading, setExecutionHistoryLoading] = useState(false);
    const [executionHistoryError, setExecutionHistoryError] = useState("");
    const [buySellTradeActionId, setBuySellTradeActionId] = useState("");
    const [expandedExecutionTradeId, setExpandedExecutionTradeId] = useState("");
    const [buySellExecutionCapabilities, setBuySellExecutionCapabilities] = useState({
        paperExecutionEnabled: true,
        liveExecutionEnabled: false,
    });
    const [buySellExecutionSettings, setBuySellExecutionSettings] = useState({
        ...DEFAULT_BUY_SELL_EXECUTION_SETTINGS,
    });
    const [preOpenChart, setPreOpenChart] = useState({
        isOpen: false,
        isLoading: false,
        symbol: "",
        support: null,
        resistance: null,
        data: null,
        error: "",
    });
    const [expandedPreOpenRowKey, setExpandedPreOpenRowKey] = useState(null);
    const [strongSignalFilters, setStrongSignalFilters] = useState([]);
    const [isStrongSignalSectorFilterEnabled, setIsStrongSignalSectorFilterEnabled] = useState(false);
    const [isOgsrFilterEnabled, setIsOgsrFilterEnabled] = useState(false);
    const [isOlsrFilterEnabled, setIsOlsrFilterEnabled] = useState(false);
    const [isLtpg500FilterEnabled, setIsLtpg500FilterEnabled] = useState(false);
    const [isLtpgsrFilterEnabled, setIsLtpgsrFilterEnabled] = useState(false);
    const [isLtplsrFilterEnabled, setIsLtplsrFilterEnabled] = useState(false);
    const [isStrongSignalFilterOpen, setIsStrongSignalFilterOpen] = useState(false);
    const [oiPercentSortDirection, setOiPercentSortDirection] = useState(null);
    const [livePercentSortDirection, setLivePercentSortDirection] = useState(null);
    const [resistanceSupportSortDirection, setResistanceSupportSortDirection] = useState(null);

    const toggleStrongSignalFilter = (signal) => {
        setStrongSignalFilters((currentFilters) =>
            currentFilters.includes(signal)
                ? currentFilters.filter((filterValue) => filterValue !== signal)
                : [...currentFilters, signal]
        );
    };

    const toggleOiPercentSort = () => {
        setLivePercentSortDirection(null);
        setResistanceSupportSortDirection(null);
        setOiPercentSortDirection((currentDirection) =>
            currentDirection === "asc"
                ? "desc"
                : currentDirection === "desc"
                    ? null
                    : "asc"
        );
    };

    const toggleLivePercentSort = () => {
        setOiPercentSortDirection(null);
        setResistanceSupportSortDirection(null);
        setLivePercentSortDirection((currentDirection) =>
            currentDirection === "asc"
                ? "desc"
                : currentDirection === "desc"
                    ? null
                    : "asc"
        );
    };

    const toggleResistanceSupportSort = () => {
        setOiPercentSortDirection(null);
        setLivePercentSortDirection(null);
        setResistanceSupportSortDirection((currentDirection) =>
            currentDirection === "asc"
                ? "desc"
                : currentDirection === "desc"
                    ? null
                    : "asc"
        );
    };

    const closePreOpenChart = () => {
        setPreOpenChart((currentChart) => ({
            ...currentChart,
            isOpen: false,
        }));
    };

    const openPreOpenChart = async (row) => {
        setPreOpenChart({
            isOpen: true,
            isLoading: true,
            symbol: row.symbol,
            support: toNumber(row.support),
            resistance: toNumber(row.resistance),
            data: null,
            error: "",
        });

        try {
            const query = new URLSearchParams({
                index: row.chartIndex || `${row.symbol}EQN`,
                preopen: "true",
            });
            const response = await fetch(`${STOCK_PRE_OPEN_CHART_DATA_URL}?${query.toString()}`);

            if (!response.ok) {
                throw new Error(`Chart request failed (${response.status})`);
            }

            const payload = await response.json();

            if (payload?.status === false) {
                throw new Error(payload?.message || "Unable to load chart data.");
            }

            const chartData = payload?.data ?? payload;
            const graphData = (Array.isArray(chartData?.grapthData)
                ? chartData.grapthData
                : []).filter((point) =>
                    Array.isArray(point) &&
                    Number.isFinite(Number(point[0])) &&
                    Number.isFinite(Number(point[1])) &&
                    Number(point[1]) !== 0
                );

            setPreOpenChart((currentChart) => currentChart.symbol === row.symbol
                ? {
                    ...currentChart,
                    isLoading: false,
                    data: { ...chartData, grapthData: graphData },
                    error: graphData.length === 0 ? "No pre-open chart data available." : "",
                }
                : currentChart
            );
        } catch (error) {
            setPreOpenChart((currentChart) => currentChart.symbol === row.symbol
                ? {
                    ...currentChart,
                    isLoading: false,
                    error: error?.message || "Unable to load chart data.",
                }
                : currentChart
            );
        }
    };

    useEffect(() => {
        if (!isStrongSignalFilterOpen) {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            if (
                strongSignalFilterRef.current &&
                !strongSignalFilterRef.current.contains(event.target)
            ) {
                setIsStrongSignalFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isStrongSignalFilterOpen]);

    useEffect(() => {
        if (!preOpenChart.isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") closePreOpenChart();
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [preOpenChart.isOpen]);

    useEffect(() => {
        if (!entryHistoryPopup) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setEntryHistoryPopup(null);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [entryHistoryPopup]);

    useEffect(() => {
        if (!rankHistoryPopup) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setRankHistoryPopup(null);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [rankHistoryPopup]);

    useEffect(() => {
        setSectorData(
            Array.isArray(sectorialIndicesData)
                ? sectorialIndicesData.filter((sector) => !isMediaSector(sector))
                : []
        );
    }, [sectorialIndicesData]);

    useEffect(() => {
        let active = true;

        if (!user?.userId) {
            setBuySellBrokers([]);
            setSelectedBuySellBrokerId("");
            setBuySellBrokerLoading(false);
            return undefined;
        }

        setBuySellBrokerLoading(true);
        setBuySellBrokerLoadError(false);
        getBrokerAccounts(user.userId)
            .then((data) => {
                if (!active) return;

                const availableBrokers = Array.isArray(data) ? data : [];
                setBuySellBrokers(availableBrokers);
                setSelectedBuySellBrokerId((currentBrokerId) => {
                    const currentBroker = availableBrokers.find(
                        (broker) => String(getBrokerId(broker)) === currentBrokerId
                    );
                    const connectedBroker = isBrokerReady(currentBroker)
                        ? currentBroker
                        : availableBrokers.find(isBrokerReady);
                    return connectedBroker ? String(getBrokerId(connectedBroker)) : "";
                });
            })
            .catch(() => {
                if (!active) return;
                setBuySellBrokers([]);
                setSelectedBuySellBrokerId("");
                setBuySellBrokerLoadError(true);
            })
            .finally(() => {
                if (active) setBuySellBrokerLoading(false);
            });

        return () => {
            active = false;
        };
    }, [user?.userId]);

    useEffect(() => {
        let active = true;

        if (!user?.accessToken) {
            setBuySellExecutionCapabilities({
                paperExecutionEnabled: true,
                liveExecutionEnabled: false,
            });
            return undefined;
        }

        getSectorStrategyExecutionCapabilities(user?.accessToken)
            .then((capabilities) => {
                if (!active || !capabilities) return;
                setBuySellExecutionCapabilities({
                    paperExecutionEnabled: capabilities.paperExecutionEnabled !== false,
                    liveExecutionEnabled: capabilities.liveExecutionEnabled === true,
                });
            })
            .catch(() => {
                if (!active) return;
                setBuySellExecutionCapabilities({
                    paperExecutionEnabled: true,
                    liveExecutionEnabled: false,
                });
            });

        return () => {
            active = false;
        };
    }, [user?.accessToken]);

    useEffect(() => {
        let active = true;

        if (!user?.userId || !user?.accessToken) {
            setBuySellStrategyId("");
            setBuySellActivatedTradingMode("");
            setBuySellTrades([]);
            return undefined;
        }

        getSectorStrategies(user.userId, user.accessToken)
            .then(async (strategies) => {
                if (!active) return;
                const strategySummary = selectMostRecentlySavedBuySellStrategy(strategies);
                if (!strategySummary) {
                    setBuySellStrategyId("");
                    setBuySellStrategyStatus("DRAFT");
                    setBuySellActivatedTradingMode("");
                    return;
                }

                const strategy = strategySummary.id
                    ? await getSectorStrategyById(
                        strategySummary.id,
                        user.userId,
                        user.accessToken
                    ) ?? strategySummary
                    : strategySummary;
                if (!active) return;

                setBuySellStrategyId(strategy.id ?? "");
                setBuySellStrategyName(strategy.name ?? "");
                setBuySellStrategyStatus(strategy.status ?? "DRAFT");
                setBuySellActivatedTradingMode(
                    strategy.status === "ACTIVE" && strategy.autoExecutionEnabled === true
                        ? strategy.executionSettings?.tradingMode ?? ""
                        : ""
                );
                setBuySellExecutionSettings(executionSettingsFromApi(
                    strategy.executionSettings
                ));
                setBuySellStrategySaved(true);
                setBuySellStrategyMessage("Saved strategy loaded from backend.");
            })
            .catch((error) => {
                if (!active) return;
                setBuySellStrategySaveError(getApiErrorMessage(
                    error,
                    "Unable to load saved strategy."
                ));
            });

        return () => {
            active = false;
        };
    }, [user?.accessToken, user?.userId]);

    useEffect(() => {
        let active = true;
        let firstLoad = true;

        if (!buySellStrategyId || !user?.userId || !user?.accessToken) {
            setBuySellTrades([]);
            setBuySellTradesLoading(false);
            return undefined;
        }

        const refreshTrades = async () => {
            if (firstLoad) setBuySellTradesLoading(true);
            try {
                const trades = await getSectorStrategyTrades(
                    buySellStrategyId,
                    user.userId,
                    user.accessToken
                );
                if (active) setBuySellTrades(sortExecutionTrades(trades));
            } catch (error) {
                if (active && firstLoad) {
                    setBuySellStrategySaveError(getApiErrorMessage(
                        error,
                        "Unable to load strategy executions."
                    ));
                }
            } finally {
                if (active && firstLoad) setBuySellTradesLoading(false);
                firstLoad = false;
            }
        };

        refreshTrades();
        const intervalId = window.setInterval(refreshTrades, 5000);
        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [buySellStrategyId, user?.accessToken, user?.userId]);

    useEffect(() => {
        let active = true;

        if (!buySellStrategyId || !user?.userId || !user?.accessToken) {
            setBuySellEntryCalculations([]);
            setBuySellCandidateRows({});
            return undefined;
        }

        const refreshEntryCalculations = async () => {
            try {
                const [calculations, candidates] = await Promise.all([
                    getSectorStrategyEntryCalculations(
                        buySellStrategyId, user.userId, user.accessToken
                    ),
                    getSectorStrategyCandidates(
                        buySellStrategyId, user.userId, user.accessToken
                    ),
                ]);
                if (active) {
                    setBuySellEntryCalculations(
                        Array.isArray(calculations) ? calculations : []
                    );
                    setBuySellCandidateRows(candidates && typeof candidates === "object"
                        ? candidates : {});
                }
            } catch {
                if (active) {
                    setBuySellEntryCalculations([]);
                    setBuySellCandidateRows({});
                }
            }
        };

        refreshEntryCalculations();
        const intervalId = window.setInterval(refreshEntryCalculations, 5000);
        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [buySellStrategyId, user?.accessToken, user?.userId]);

    useEffect(() => {
        if (!latestExecutionTrade?.id ||
            latestExecutionTrade.strategyId !== buySellStrategyId ||
            latestExecutionTrade.tradeDate !== getMarketDateString()) return;

        setBuySellTrades((currentTrades) => mergeExecutionTrade(
            currentTrades,
            latestExecutionTrade
        ));
    }, [buySellStrategyId, latestExecutionTrade]);

    useEffect(() => {
        let active = true;

        if (buySellSettingsTab !== "HISTORY" || !user?.userId || !user?.accessToken) {
            return undefined;
        }

        if (!executionHistoryStartDate || !executionHistoryEndDate) {
            setExecutionHistoryTrades([]);
            setExecutionHistoryError("");
            setExecutionHistoryLoading(false);
            return undefined;
        }
        if (executionHistoryStartDate > executionHistoryEndDate) {
            setExecutionHistoryTrades([]);
            setExecutionHistoryError("Start date must not be after end date.");
            setExecutionHistoryLoading(false);
            return undefined;
        }

        setExecutionHistoryLoading(true);
        setExecutionHistoryError("");
        const historyRequest = executionHistoryStartDate === executionHistoryEndDate
            ? getSectorStrategyExecutionHistory(
                user.userId,
                executionHistoryStartDate,
                user.accessToken
            )
            : getSectorStrategyExecutionHistoryForRange(
                user.userId,
                executionHistoryStartDate,
                executionHistoryEndDate,
                user.accessToken
            );
        historyRequest.then((history) => {
            if (active) {
                setExecutionHistoryTrades(sortExecutionTrades(history));
            }
        }).catch((error) => {
            if (active) {
                setExecutionHistoryTrades([]);
                setExecutionHistoryError(getApiErrorMessage(
                    error,
                    "Unable to load execution history."
                ));
            }
        }).finally(() => {
            if (active) setExecutionHistoryLoading(false);
        });

        return () => {
            active = false;
        };
    }, [buySellSettingsTab, executionHistoryStartDate, executionHistoryEndDate,
        user?.accessToken, user?.userId]);

    useEffect(() => {
        if (buySellSettingsTab !== "HISTORY" || !latestExecutionTrade?.id ||
            !latestExecutionTrade?.tradeDate ||
            latestExecutionTrade.tradeDate < executionHistoryStartDate ||
            latestExecutionTrade.tradeDate > executionHistoryEndDate) return;

        setExecutionHistoryTrades((currentTrades) => mergeExecutionTrade(
            currentTrades,
            latestExecutionTrade
        ));
    }, [buySellSettingsTab, executionHistoryStartDate, executionHistoryEndDate,
        latestExecutionTrade]);

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const updateLayoutMetrics = () => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        };

        updateLayoutMetrics();

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateLayoutMetrics)
            : null;

        resizeObserver?.observe(stickyTopShellElement);
        window.addEventListener("resize", updateLayoutMetrics);
        window.addEventListener("orientationchange", updateLayoutMetrics);
        window.visualViewport?.addEventListener("resize", updateLayoutMetrics);
        window.visualViewport?.addEventListener("scroll", updateLayoutMetrics);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateLayoutMetrics);
            window.removeEventListener("orientationchange", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("resize", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("scroll", updateLayoutMetrics);
        };
    }, []);

    useEffect(() => {
        setMidCrossScannerData(
            atlasScannerData && typeof atlasScannerData === "object" && !Array.isArray(atlasScannerData)
                ? atlasScannerData
                : {}
        );

        if (
            atlasScannerData &&
            typeof atlasScannerData === "object" &&
            !Array.isArray(atlasScannerData) &&
            Object.keys(atlasScannerData).length > 0
        ) {
            console.log("Mid Cross Scanner Data:", atlasScannerData);
        }
    }, [atlasScannerData]);

    const getChangeCellClassName = (changePercent) => {
        if (changePercent > 0) return "positive";
        if (changePercent < 0) return "negative";
        return "neutral";
    };

    const getTopAndBottomCHP = (sectors) => {
        if (!Array.isArray(sectors) || sectors.length === 0) return {};

        const sorted = sectors
            .map((sector) => ({
                symbol: sector?.index,
                chp: toNumber(sector?.pChange),
            }))
            .filter((sector) => sector.chp != null)
            .sort((a, b) => a.chp - b.chp);

        if (sorted.length === 0) return {};

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            thirdLowest: sorted[2],
            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
            thirdHighest: sorted[sorted.length - 3],
        };
    };

    const getScannerHeaderSymbol = (value) =>
        String(value ?? "")
            .split(":")
            .pop()
            ?.split("-")[0]
            ?.trim() || "--";

    const getChpColor = (value) => {
        const num = toNumber(value);
        if (num == null) return "white";
        if (num > 0) return "#0dc80d";
        if (num < 0) return "rgb(228, 67, 136)";
        return "white";
    };

    const stockDayRangeList = Array.isArray(options?.stockDayRangeModelList)
        ? options.stockDayRangeModelList
        : [];
    const lotSizeBySymbol = createLotSizeBySymbolMap(stockDayRangeList);
    const hasMidCrossData = hasScannerEntries(midCrossScannerData);
    const allSectorDetailsBySymbol = createSectorDetailsMap(sectorData, () => true, false);
    const midCrossBuyRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_BUY);
    const midCrossSellRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_SELL);
    const midCrossBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossBuyRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const midCrossSellStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossSellRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const foTopGainerRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_GAINERS");
    const foTopLooserRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_LOOSERS");
    const foTopGainerSymbolSet = createSymbolSet(foTopGainerRows);
    const foTopLooserSymbolSet = createSymbolSet(foTopLooserRows);
    const rankedSectors = getRankedSectors(sectorData);
    const highestThreeSectors = rankedSectors.slice(-3).map(({ sector }) => sector);
    const lowestThreeSectors = rankedSectors.slice(0, 3).map(({ sector }) => sector);
    const highestThreeSectorSymbolSet = createSectorSymbolSet(highestThreeSectors);
    const lowestThreeSectorSymbolSet = createSectorSymbolSet(lowestThreeSectors);
    const topBottomSectorSymbolSet = new Set([
        ...highestThreeSectorSymbolSet,
        ...lowestThreeSectorSymbolSet,
    ]);
    const highestThreeSectorNameSet = createSectorNameSet(highestThreeSectors);
    const lowestThreeSectorNameSet = createSectorNameSet(lowestThreeSectors);
    const mcBuyFilterStocks = filterStocksByFoTopAndSectors(
        midCrossBuyStocks,
        foTopGainerSymbolSet,
        highestThreeSectorSymbolSet,
        highestThreeSectorNameSet
    );
    const mcSellFilterStocks = filterStocksByFoTopAndSectors(
        midCrossSellStocks,
        foTopLooserSymbolSet,
        lowestThreeSectorSymbolSet,
        lowestThreeSectorNameSet
    );
    const stockAnalysisBySymbol = createStockAnalysisBySymbolMap(stockAnalysisData);
    const stockPreOpenRows = normalizeStockPreOpenRows(stockPreOpenData).map((row) => {
        const normalizedRowSymbol = normalizeSymbol(row.symbol);
        const stockAnalysis = stockAnalysisBySymbol.get(normalizedRowSymbol) ?? {};
        const sectorDetails = allSectorDetailsBySymbol.get(normalizedRowSymbol) ?? {};

        return {
            ...row,
            ltp: stockAnalysis.ltp,
            firstFiveMinuteHigh: stockAnalysis.firstFiveMinuteHigh,
            firstFiveMinuteLow: stockAnalysis.firstFiveMinuteLow,
            avgInOI: stockAnalysis.avgInOI ?? row.avgInOI,
            chp: stockAnalysis.chp ?? row.chp,
            support: stockAnalysis.support ?? row.support,
            resistance: stockAnalysis.resistance ?? row.resistance,
            sectorName: sectorDetails.sectorName ?? row.sectorName,
            sectorPercent: sectorDetails.sectorPercent ?? row.sectorPercent,
        };
    });

    useEffect(() => {
        console.log("stockPreOpenData:", stockPreOpenData);
    }, [stockPreOpenData]);

    const podAduCount = getStockPreOpenAduCount(stockPreOpenData);
    const podAdvancesCount = formatAduCountValue(podAduCount.advances);
    const podDeclinesCount = formatAduCountValue(podAduCount.declines);
    const podUnchangedCount = formatAduCountValue(podAduCount.unchange ?? podAduCount.unchanged);
    const podSelectedFilterCount =
        strongSignalFilters.length +
        Number(isStrongSignalSectorFilterEnabled) +
        Number(isLtpgsrFilterEnabled) +
        Number(isLtplsrFilterEnabled) +
        Number(isOgsrFilterEnabled) +
        Number(isOlsrFilterEnabled) +
        Number(isLtpg500FilterEnabled);
    const podFilteredStockCount = stockPreOpenRows.filter((row) => {
        const symbol = normalizeSymbol(row.symbol);
        const ltp = toNumber(row.ltp);
        const finalPrice = toNumber(row.finalPrice);
        const support = toNumber(row.support);
        const resistance = toNumber(row.resistance);
        const matchesSignal = strongSignalFilters.length === 0 ||
            strongSignalFilters.includes(getStrongSignal(row));
        const matchesSector = !isStrongSignalSectorFilterEnabled ||
            topBottomSectorSymbolSet.has(symbol);
        const matchesOpenRange = (!isOgsrFilterEnabled && !isOlsrFilterEnabled) ||
            (finalPrice != null && support != null && resistance != null && (
                (isOgsrFilterEnabled && finalPrice >= support && finalPrice >= resistance) ||
                (isOlsrFilterEnabled && finalPrice <= support && finalPrice <= resistance)
            ));
        const matchesLtpRange = (!isLtpgsrFilterEnabled && !isLtplsrFilterEnabled) ||
            (ltp != null && support != null && resistance != null && (
                (isLtpgsrFilterEnabled && ltp >= support && ltp >= resistance) ||
                (isLtplsrFilterEnabled && ltp <= support && ltp <= resistance)
            ));
        const matchesLtp500 = !isLtpg500FilterEnabled || (ltp != null && ltp >= 500);

        return matchesSignal && matchesSector && matchesOpenRange &&
            matchesLtpRange && matchesLtp500;
    }).length;

    const midCrossBuyCount = midCrossBuyStocks.length;
    const midCrossSellCount = midCrossSellStocks.length;
    const mcFilterCount = mcBuyFilterStocks.length + mcSellFilterStocks.length;
    const stockPreOpenCount = stockPreOpenRows.length;
    const rsiBandBullishRows = getRsiBandRows(sectorRsiBandScannerData, "bullish");
    const rsiBandBearishRows = getRsiBandRows(sectorRsiBandScannerData, "bearish");
    const rsiBandScanCount = rsiBandBullishRows.length + rsiBandBearishRows.length;
    const activeFixedTitle = activeTab === MID_CROSS_BUY_TAB
        ? {
            label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_BUY],
            count: midCrossBuyCount,
        }
        : activeTab === MID_CROSS_SELL_TAB
            ? {
                label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_SELL],
                count: midCrossSellCount,
            }
            : activeTab === ALL_STOCK_DATA_TAB
                ? {
                    label: SCANNER_SECTION_TITLE_MAP[ALL_STOCK_DATA_TAB],
                    count: allStockDataRows.length,
                }
            : activeTab === VAH_VAL_TAB
                ? {
                    label: SCANNER_SECTION_TITLE_MAP[VAH_VAL_TAB],
                    count: vahBreakoutRows.length + valBreakdownRows.length,
                }
            : activeTab === POD_TAB
                ? {
                    label: SCANNER_SECTION_TITLE_MAP[POD_TAB],
                    count: stockPreOpenCount,
                }
            : null;

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [activeTab, midCrossBuyCount, midCrossSellCount, mcFilterCount, stockPreOpenCount, rsiBandScanCount]);
    const sectorHeaderData = sectorData[0] ?? {};
    const headerDate = sectorHeaderData?.localDate ?? sectorHeaderData?.createdDate ?? sectorHeaderData?.date ?? "--";
    const headerTime = sectorHeaderData?.localTime ?? sectorHeaderData?.createdTime ?? sectorHeaderData?.time ?? "--";
    const scannerHeaderStats = getTopAndBottomCHP(sectorData);
    const topBottomHeaderData = [
        {
            label1: "1st HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondHighest?.chp ?? "--",
        },
        {
            label1: "3rd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondLowest?.chp ?? "--",
        },
        {
            label1: "3rd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdLowest?.chp ?? "--",
        },
    ];
    const renderTableRows = (stocks, emptyMessage, showCount = true, highlightedSymbolSet = new Set()) => {
        if (stocks.length === 0) {
            return (
                <tr>
                    <td colSpan={showCount ? "10" : "9"} className="stock-scanner-empty-cell">
                        {emptyMessage}
                    </td>
                </tr>
            );
        }

        return stocks.map((stock) => {
            const changePercentValue = stock.changePercent ?? 0;
            const isPositive = changePercentValue >= 0;
            const normalizedSymbol = normalizeSymbol(stock.symbol);
            const isHighlightedSymbol = highlightedSymbolSet.has(normalizedSymbol);
            const sectorPercentValue = toNumber(stock.sectorPercent);

            return (
                <tr key={stock.__scannerKey}>
                    <td>{stock.triggerDate}</td>
                    <td>{stock.triggerTime}</td>
                    <td className={isHighlightedSymbol ? "symbol-highlight" : ""}>{stock.symbol}</td>
                    <td>{formatNumber(stock.ltp)}</td>
                    <td className={getChangeCellClassName(changePercentValue)}>
                        {isPositive ? "+" : ""}
                        {formatNumber(changePercentValue)}%
                    </td>
                    {showCount && <td>{stock.symbolCount}</td>}
                    <td>{formatSectorDisplayName(stock.sectorName)}</td>
                    <td className={sectorPercentValue == null ? "" : getChangeCellClassName(sectorPercentValue)}>
                        {sectorPercentValue == null
                            ? "--"
                            : `${sectorPercentValue >= 0 ? "+" : ""}${formatNumber(sectorPercentValue)}%`}
                    </td>
                    <td>{stock.fnoLotSize ?? "--"}</td>
                    <td>{formatNumber(stock.volume)}</td>
                </tr>
            );
        });
    };

    const scannerWaitingMessage = hasMidCrossData
        ? "No matching stocks found."
        : "Waiting for live scanner data...";
    const hasSectorData = sectorData.length > 0;
    const mcBuyFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopGainerRows.length === 0
            ? "Waiting for live F&O top gainers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";
    const mcSellFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopLooserRows.length === 0
            ? "Waiting for live F&O top loosers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";

    const renderScannerTable = (
        stocks,
        emptyMessage,
        showCount = true,
        highlightedSymbolSet = new Set(),
        useContentHeight = false
    ) => (
        <div className={`table-scroll-area ${useContentHeight ? "mc-filter-table-scroll-area" : ""}`}>
            <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table scanner-table">
                <thead>
                    <tr>
                        <th>DATE</th>
                        <th>TIME</th>
                        <th>SYMBOL</th>
                        <th>LTP</th>
                        <th>CHANGE (%)</th>
                        {showCount && <th>COUNT</th>}
                        <th>SECTOR</th>
                        <th>SECTOR (%)</th>
                        <th>FNO LOT SIZE</th>
                        <th>VOLUME</th>
                    </tr>
                </thead>
                <tbody>{renderTableRows(stocks, emptyMessage, showCount, highlightedSymbolSet)}</tbody>
            </table>
        </div>
    );

    const renderScannerSection = (
        sectionKey,
        stocks,
        emptyMessage = scannerWaitingMessage,
        showTitle = true,
        useContentHeight = false
    ) => (
        <div className="scanner-section-list">
            <div className="scanner-section-block">
                {showTitle && (
                    <div className="scanner-section-title">
                        {SCANNER_SECTION_TITLE_MAP[sectionKey]} ({stocks.length})
                    </div>
                )}
                {renderScannerTable(stocks, emptyMessage, false, new Set(), useContentHeight)}
            </div>
        </div>
    );

    const renderRsiBandSection = (sectionKey, rows) => (
        <div className="scanner-section-list">
            <div className="scanner-section-block">
                <div className="scanner-section-title">
                    {SCANNER_SECTION_TITLE_MAP[sectionKey]} ({rows.length})
                </div>
                <div className="table-scroll-area mc-filter-table-scroll-area">
                    <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table scanner-table">
                        <thead>
                            <tr>
                                <th>SYMBOL</th>
                                <th>SIDE</th>
                                <th>SECTOR</th>
                                <th>EMA 200 STATUS</th>
                                <th>REMARKS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? rows.map((row, index) => (
                                <tr key={`${row?.symbol ?? sectionKey}-${index}`}>
                                    <td>{row?.symbol ?? "--"}</td>
                                    <td className={normalizeScannerKey(row?.side) === "BUY" ? "positive" : "negative"}>
                                        {row?.side ?? "--"}
                                    </td>
                                    <td>{formatSectorDisplayName(row?.sector)}</td>
                                    <td>{row?.ema200Status ?? "--"}</td>
                                    <td>{row?.remarks ?? "--"}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="stock-scanner-empty-cell">
                                        Waiting for RSI band scanner data...
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const getStrategyRows = (key) => Array.isArray(sectorStrategiesData?.[key])
        ? sectorStrategiesData[key]
        : [];

    const liveRankHistoryPopup = rankHistoryPopup
        ? (() => {
            const currentRows = getStrategyRows(rankHistoryPopup.listKey);
            const currentIndex = currentRows.findIndex((row) =>
                normalizeSymbol(row?.symbol) === rankHistoryPopup.symbol
            );

            if (currentIndex < 0) {
                return {
                    ...rankHistoryPopup,
                    isCurrentlyRanked: false,
                };
            }

            const currentRow = currentRows[currentIndex];
            return {
                ...rankHistoryPopup,
                isCurrentlyRanked: true,
                rank: toNumber(currentRow?.rank) ?? currentIndex + 1,
                rankChange: toNumber(currentRow?.rankChange) ?? 0,
                previousRank: toNumber(currentRow?.previousRank),
                entryDate: currentRow?.rankEntryDate,
                entryTime: currentRow?.rankEntryTime,
                entryCount: currentRow?.rankEntryCount ?? 0,
                ltp: currentRow?.lp,
                changePercentage: currentRow?.chp,
                history: Array.isArray(currentRow?.rankHistory)
                    ? [...currentRow.rankHistory].reverse()
                    : [],
            };
        })()
        : null;

    const updateBuySellExecutionSetting = (field, value) => {
        setBuySellStrategySaved(false);
        setBuySellStrategySaveError("");
        setBuySellStrategyMessage("");
        setBuySellExecutionSettings((currentSettings) => ({
            ...currentSettings,
            [field]: value,
        }));
    };

    const updateBuySellEntryCondition = (level, field, value) => {
        setBuySellStrategySaved(false);
        setBuySellStrategySaveError("");
        setBuySellStrategyMessage("");
        setBuySellExecutionSettings((currentSettings) => ({
            ...currentSettings,
            entryConditions: (currentSettings.entryConditions ?? DEFAULT_ENTRY_CONDITIONS)
                .map((condition) => condition.level === level
                    ? { ...condition, [field]: value }
                    : condition),
        }));
    };

    const updateBuySellEntryConditionGroupSlippage = (levels, value) => {
        setBuySellStrategySaved(false);
        setBuySellStrategySaveError("");
        setBuySellStrategyMessage("");
        setBuySellExecutionSettings((currentSettings) => ({
            ...currentSettings,
            entryConditions: (currentSettings.entryConditions ?? DEFAULT_ENTRY_CONDITIONS)
                .map((condition) => levels.includes(condition.level)
                    ? { ...condition, slippagePercent: value }
                    : condition),
        }));
    };

    const adjustBuySellEntryConditionGroupSlippage = (levels, currentValue, delta) => {
        const numericValue = toNumber(currentValue) ?? 0;
        const adjustedValue = Math.min(100, Math.max(0.01, numericValue + delta));
        updateBuySellEntryConditionGroupSlippage(
            levels,
            String(Number(adjustedValue.toFixed(2)))
        );
    };

    const adjustBuySellIntradayRangePercent = (currentValue, delta) => {
        const numericValue = toNumber(currentValue) ?? 0;
        const adjustedValue = Math.min(100, Math.max(0.01, numericValue + delta));
        updateBuySellExecutionSetting(
            "maximumIntradayRangePercent",
            String(Number(adjustedValue.toFixed(2)))
        );
    };

    const isBuySellExecutionSettingsValid = (settings = buySellExecutionSettings) => {
        const startMinutes = timeToMinutes(settings.startTime);
        const endMinutes = timeToMinutes(settings.endTime);
        const squareOffMinutes = timeToMinutes(settings.squareOffTime);
        const optionalNonNegativeFields = [
            settings.stopLoss,
            settings.target,
            settings.maximumDailyLoss,
            settings.maximumOrderValue,
            settings.maximumSlippagePercent,
            settings.maximumIntradayRangePercent,
        ];
        const entryConditions = Array.isArray(settings.entryConditions)
            ? settings.entryConditions
            : [];
        const enabledEntryConditions = entryConditions.filter(
            (condition) => condition.enabled
        );
        const entryConditionsValid = enabledEntryConditions.length > 0 &&
            enabledEntryConditions.every((condition) => {
                const slippage = toNumber(condition.slippagePercent);
                return slippage != null && slippage > 0 && slippage <= 100;
            });

        return (toNumber(settings.defaultLots) ?? 0) >= 1 &&
            (toNumber(settings.maximumTradesPerDay) ?? 0) >= 1 &&
            (toNumber(settings.maximumOpenPositions) ?? 0) >= 1 &&
            startMinutes != null && endMinutes != null && squareOffMinutes != null &&
            startMinutes < endMinutes && endMinutes <= squareOffMinutes &&
            optionalNonNegativeFields.every(
                (value) => String(value ?? "").trim() === "" || (toNumber(value) ?? -1) >= 0
            ) &&
            entryConditionsValid &&
            (settings.allowBuy || settings.allowSell);
    };

    const saveBuySellStrategy = ({
        status = "DRAFT",
        autoExecutionEnabled = false,
        executionSettings = buySellExecutionSettings,
        successMessage = "Strategy draft saved in backend.",
    } = {}) => {
        const normalizedStrategyName = buySellStrategyName.trim();
        const selectedBroker = buySellBrokers.find(
            (broker) => String(getBrokerId(broker)) === selectedBuySellBrokerId
        );
        const isLiveActivation = autoExecutionEnabled && status === "ACTIVE" &&
            executionSettings.tradingMode === "LIVE";

        if (!user?.userId || !normalizedStrategyName) {
            setBuySellStrategySaveError(
                "A signed-in user is required to save execution settings."
            );
            return Promise.resolve(false);
        }
        if (isLiveActivation && !selectedBroker) {
            setBuySellStrategySaveError(
                "Connect a broker from the Broker page before using LIVE execution."
            );
            return Promise.resolve(false);
        }

        const integerValue = (value, fallback) => Math.max(
            1,
            Math.trunc(toNumber(value) ?? fallback)
        );
        const decimalValue = (value) => toNumber(value) ?? 0;
        const normalizedEntryConditions = (executionSettings.entryConditions ?? [])
            .map((condition) => ({
                level: condition.level,
                enabled: Boolean(condition.enabled),
                slippagePercent: decimalValue(condition.slippagePercent),
            }));
        const primaryEntryCondition = normalizedEntryConditions.find(
            (condition) => condition.enabled
        ) ?? { level: "WH", slippagePercent: 1.5 };
        const normalizedExecutionSettings = {
            ...executionSettings,
            defaultLots: integerValue(executionSettings.defaultLots, 1),
            stopLoss: decimalValue(executionSettings.stopLoss),
            target: decimalValue(executionSettings.target),
            maximumDailyLoss: decimalValue(executionSettings.maximumDailyLoss),
            maximumTradesPerDay: integerValue(
                executionSettings.maximumTradesPerDay,
                5
            ),
            maximumOpenPositions: integerValue(
                executionSettings.maximumOpenPositions,
                3
            ),
            topGainerLoserCount: integerValue(
                executionSettings.topGainerLoserCount,
                20
            ),
            maximumOrderValue: decimalValue(executionSettings.maximumOrderValue),
            maximumSlippagePercent: decimalValue(
                executionSettings.maximumSlippagePercent
            ),
            maximumIntradayRangePercent: decimalValue(
                executionSettings.maximumIntradayRangePercent
            ),
            fiveMinuteRangeEnabled: executionSettings.fiveMinuteRangeEnabled !== false,
            fifteenMinuteRangeEnabled: executionSettings.fifteenMinuteRangeEnabled !== false,
            entryLevel: primaryEntryCondition.level,
            maximumEntryDistancePercent: primaryEntryCondition.slippagePercent,
            entryConditions: normalizedEntryConditions,
        };

        const executeSave = async () => {
            setBuySellStrategySaving(true);
            setBuySellStrategySaveError("");
            setBuySellStrategyMessage("");
            try {
            const payload = {
                name: normalizedStrategyName,
                userId: user.userId,
                brokerAccountId: selectedBroker ? getBrokerId(selectedBroker) : null,
                strategyType: "SECTOR_STRATEGY_BUY_SELL",
                strategyVersion: "SECTOR_STRATEGY_BUY_SELL_V1",
                status,
                autoExecutionEnabled,
                exchange: "NSE",
                entryRule: "CONFIGURABLE_LEVEL_RETEST",
                executionSettings: normalizedExecutionSettings,
                stocks: [],
            };
            const saveResponseStrategy = buySellStrategyId
                ? await updateSectorStrategy(
                    buySellStrategyId,
                    user.userId,
                    payload,
                    user.accessToken
                )
                : await saveSectorStrategy(payload, user.accessToken);

            const persistedStrategyId = saveResponseStrategy?.id ?? buySellStrategyId;
            let savedStrategy = saveResponseStrategy;
            if (persistedStrategyId) {
                try {
                    savedStrategy = await getSectorStrategyById(
                        persistedStrategyId,
                        user.userId,
                        user.accessToken
                    ) ?? saveResponseStrategy;
                } catch {
                    // The save succeeded. Keep the save response and the submitted
                    // values if the verification read is temporarily unavailable.
                }
            }

            const savedStatus = savedStrategy?.status ?? status;
            const savedAutoExecutionEnabled = savedStrategy?.autoExecutionEnabled ??
                autoExecutionEnabled;
            const savedExecutionSettings = executionSettingsFromApi(
                savedStrategy?.executionSettings ?? {},
                normalizedExecutionSettings
            );
            setBuySellStrategyId(savedStrategy?.id ?? persistedStrategyId ?? "");
            setBuySellExecutionSettings(savedExecutionSettings);
            setBuySellStrategySaved(true);
            setBuySellStrategyStatus(savedStatus);
            setBuySellActivatedTradingMode(
                savedStatus === "ACTIVE" && savedAutoExecutionEnabled === true
                    ? savedExecutionSettings.tradingMode ?? ""
                    : ""
            );
            if (persistedStrategyId) {
                try {
                    const calculations = await getSectorStrategyEntryCalculations(
                        persistedStrategyId,
                        user.userId,
                        user.accessToken
                    );
                    setBuySellEntryCalculations(
                        Array.isArray(calculations) ? calculations : []
                    );
                } catch {
                    // The regular 5-second refresh retries live entry calculations.
                }
            }
            setBuySellStrategyMessage(successMessage);
            return true;
        } catch (error) {
            setBuySellStrategySaved(false);
            setBuySellStrategySaveError(getApiErrorMessage(
                error,
                "Unable to save the strategy in backend."
            ));
            setBuySellStrategyMessage("");
            return false;
        } finally {
            setBuySellStrategySaving(false);
        }
        };

        return executeSave();
    };

    const activateBuySellStrategy = async (tradingMode) => {
        if (!isBuySellExecutionSettingsValid()) {
            setBuySellSettingsTab("EXECUTION_RISK");
            setBuySellStrategySaveError(
                "Complete valid execution and risk settings before activation."
            );
            return;
        }

        if (tradingMode === "PAPER" &&
            !buySellExecutionCapabilities.paperExecutionEnabled) {
            setBuySellStrategySaveError("Paper execution is disabled by the server.");
            return;
        }
        if (tradingMode === "LIVE" && !buySellExecutionCapabilities.liveExecutionEnabled) {
            setBuySellStrategySaveError(
                "LIVE execution is disabled by the stock-api server configuration."
            );
            return;
        }
        const activationBroker = buySellBrokers.find(
            (broker) => String(getBrokerId(broker)) === selectedBuySellBrokerId
        );
        if (tradingMode === "LIVE" &&
            (!activationBroker || !isBrokerReady(activationBroker))) {
            setBuySellStrategySaveError(
                "Connect a broker from the Broker page before activating LIVE execution."
            );
            return;
        }

        if (
            tradingMode === "LIVE" &&
            !window.confirm(
                "This will place real option orders through the selected broker. Activate LIVE execution?"
            )
        ) {
            return;
        }

        const nextExecutionSettings = {
            ...buySellExecutionSettings,
            tradingMode,
        };
        await saveBuySellStrategy({
            status: "ACTIVE",
            autoExecutionEnabled: true,
            executionSettings: nextExecutionSettings,
            successMessage: `${tradingMode} strategy activated in backend.`,
        });
    };

    const updateBuySellStrategyLifecycle = async (status) => {
        await saveBuySellStrategy({
            status,
            autoExecutionEnabled: false,
            successMessage: status === "PAUSED"
                ? "Strategy paused. Existing positions remain monitored."
                : "Strategy stopped. No new entries will be created.",
        });
    };

    const handleSquareOffTrade = async (trade) => {
        if (!trade?.id || !user?.userId) return;
        if (!window.confirm(`Square off ${trade.symbol} ${trade.optionType}?`)) return;
        setBuySellTradeActionId(trade.id);
        setBuySellStrategySaveError("");
        try {
            const updatedTrade = await squareOffSectorStrategyTrade(
                trade.id,
                user.userId,
                user.accessToken
            );
            setBuySellTrades((currentTrades) => currentTrades.map((currentTrade) =>
                currentTrade.id === updatedTrade?.id ? updatedTrade : currentTrade
            ));
            setBuySellStrategyMessage(isExternallyClosedTrade(updatedTrade)
                ? "Broker position was already closed. No SELL order was submitted."
                : "Square-off submitted successfully.");
        } catch (error) {
            setBuySellStrategySaveError(getApiErrorMessage(
                error,
                "Unable to square off the trade."
            ));
        } finally {
            setBuySellTradeActionId("");
        }
    };

    const handleSquareOffStrategy = async () => {
        if (!buySellStrategyId || !user?.userId) return;
        if (!window.confirm("Square off every OPEN position for this strategy?")) return;
        setBuySellTradeActionId("ALL");
        setBuySellStrategySaveError("");
        try {
            const trades = await squareOffSectorStrategy(
                buySellStrategyId,
                user.userId,
                user.accessToken
            );
            const updatedTrades = Array.isArray(trades) ? trades : [];
            const externallyClosedCount = updatedTrades.filter(isExternallyClosedTrade).length;
            setBuySellTrades(sortExecutionTrades(updatedTrades));
            setBuySellStrategyMessage(externallyClosedCount > 0
                ? `Strategy synchronized: ${externallyClosedCount} broker-closed position${
                    externallyClosedCount === 1 ? "" : "s"
                } required no SELL order.`
                : "Strategy square-off submitted successfully.");
        } catch (error) {
            setBuySellStrategySaveError(getApiErrorMessage(
                error,
                "Unable to square off strategy positions."
            ));
        } finally {
            setBuySellTradeActionId("");
        }
    };

    const renderStrategyStockTable = (
        title,
        rows,
        tone = "neutral",
        showEntryTime = false,
        rankListKey = ""
    ) => {
        const isSellTable = normalizeScannerKey(title) === "SELL";
        const isPutTable = isSellTable || rankListKey === "TOP_20_LOOSER_STOCK";
        const optionLtpHeader = isPutTable ? "PELTP" : "CELTP";
        const stockLtpHeader = "5M LTP 15M";
        const showRankHistory = Boolean(rankListKey);

        return (
        <section className={`strategy-table-card strategy-table-card--${tone}`}>
            <div className="strategy-table-title">{title} ({rows.length})</div>
            <div className="strategy-table-scroll">
                <table className="responsive-table table-bordered text-center strategy-table">
                    <thead><tr>
                        <th>SYMBOL</th>
                        {showRankHistory ? <>
                            <th>RANK</th><th>5ML</th><th>5MH</th><th>{isPutTable ? "5ML %" : "5MH %"}</th><th>15MH %</th>
                            <th>15ML</th><th>15MH</th>
                            <th>{isPutTable ? "5ML EP" : "5MH EP"}</th>
                            <th>{isPutTable ? "15ML EP" : "15MH EP"}</th>
                            <th>EMA200</th>
                            <th>{stockLtpHeader}</th><th>STOCK %</th>
                            <th>ATM</th><th>TYPE</th><th>{optionLtpHeader}</th><th>WH</th><th>WL</th><th>LOT</th>
                        </> : <>
                            <th>5MH</th><th>5ML</th><th>STOCK %</th><th>{stockLtpHeader}</th><th>EMA200</th>
                            {showEntryTime && <><th>STRIKE</th><th>TYPE</th><th>{optionLtpHeader}</th><th>ENTRY</th></>}
                            <th>SECTOR</th><th>SECTOR %</th><th>AVG OI %</th>
                            {showEntryTime && <><th>LOT</th><th>TIME</th><th>EC</th></>}
                        </>}
                    </tr></thead>
                    <tbody>{rows.length ? rows.map((row, index) => {
                        const stockPercent = toNumber(row?.chp);
                        const sectorPercent = toNumber(row?.sectorChp);
                        const ltp = toNumber(row?.lp);
                        const ema200 = toNumber(row?.ema200);
                        const ema200Direction = ltp != null && ema200 != null
                            ? ltp > ema200 ? "up" : ltp < ema200 ? "down" : null
                            : null;
                        const ema200LtpMetrics = getEma200LtpMetrics(row);
                        const ema200LtpDisplay = formatEma200LtpMetrics(ema200LtpMetrics);
                        const ema200PercentageTone = ema200LtpMetrics.differencePercent > 0
                            ? "positive"
                            : ema200LtpMetrics.differencePercent < 0 ? "negative" : "neutral";
                        const fiveMinuteHigh = toNumber(row?.firstFiveMinuteHigh);
                        const fiveMinuteLow = toNumber(row?.firstFiveMinuteLow);
                        const fifteenMinuteHigh = toNumber(row?.firstFifteenMinuteHigh);
                        const fifteenMinuteLow = toNumber(row?.firstFifteenMinuteLow);
                        const optionLtpValue = isPutTable
                            ? row?.peLtp ?? row?.optionLtp
                            : row?.ceLtp ?? row?.optionLtp;
                        const optionLtp = toNumber(optionLtpValue);
                        const previousWeekHigh = toNumber(row?.optionPreviousWeekHigh);
                        const previousWeekLow = toNumber(row?.optionPreviousWeekLow);
                        const highLevelReached = !isPutTable;
                        const fiveMinuteLevelReached = highLevelReached
                            ? ltp != null && fiveMinuteHigh != null && ltp >= fiveMinuteHigh
                            : ltp != null && fiveMinuteLow != null && ltp <= fiveMinuteLow;
                        const fifteenMinuteLevelReached = highLevelReached
                            ? ltp != null && fifteenMinuteHigh != null && ltp >= fifteenMinuteHigh
                            : ltp != null && fifteenMinuteLow != null && ltp <= fifteenMinuteLow;
                        const ltpRangeClassName = fiveMinuteLevelReached || fifteenMinuteLevelReached
                            ? highLevelReached ? "strategy-ltp-above-high" : "strategy-ltp-below-low"
                            : "";
                        const optionLtpRangeClassName = optionLtp != null && previousWeekHigh != null
                            && optionLtp >= previousWeekHigh
                            ? "strategy-ltp-above-high"
                            : optionLtp != null && previousWeekLow != null && optionLtp <= previousWeekLow
                                ? "strategy-ltp-below-low"
                                : "";
                        const rank = toNumber(row?.rank) ?? index + 1;
                        const rankChange = toNumber(row?.rankChange) ?? 0;
                        const rankHistory = Array.isArray(row?.rankHistory)
                            ? [...row.rankHistory].reverse()
                            : [];
                        const entryCalculation = showRankHistory
                            ? buySellEntryCalculations.find((calculation) =>
                                normalizeScannerKey(calculation?.listType) === normalizeScannerKey(rankListKey) &&
                                normalizeSymbol(calculation?.symbol) === normalizeSymbol(row?.symbol)
                            )
                            : null;
                        const fiveMinuteLevel = isPutTable ? "5ML" : "5MH";
                        const fiveMinuteRangePercent = toNumber(row?.firstFiveMinuteRangePercent);
                        const fiveMinuteEntryPoint = isPutTable
                            ? entryCalculation?.fiveMinuteLowEntryPrice
                            : entryCalculation?.fiveMinuteHighEntryPrice;
                        const fifteenMinuteEntryPoint = isPutTable
                            ? entryCalculation?.fifteenMinuteLowEntryPrice
                            : entryCalculation?.fifteenMinuteHighEntryPrice;
                        const fiveMinuteEntryPointValue = toNumber(fiveMinuteEntryPoint);
                        const fifteenMinuteEntryPointValue = toNumber(fifteenMinuteEntryPoint);
                        const fiveMinuteSlippagePercent = isPutTable
                            ? entryCalculation?.fiveMinuteLowSlippagePercent
                            : entryCalculation?.fiveMinuteHighSlippagePercent;
                        const fiveMinuteConditionEnabled = isPutTable
                            ? entryCalculation?.fiveMinuteLowEnabled
                            : entryCalculation?.fiveMinuteHighEnabled;
                        const fiveMinuteEntryPointReached = highLevelReached
                            ? ltp != null && fiveMinuteEntryPointValue != null
                                && ltp >= fiveMinuteEntryPointValue
                            : ltp != null && fiveMinuteEntryPointValue != null
                                && ltp <= fiveMinuteEntryPointValue;
                        const fifteenMinuteEntryPointReached = highLevelReached
                            ? ltp != null && fifteenMinuteEntryPointValue != null
                                && ltp >= fifteenMinuteEntryPointValue
                            : ltp != null && fifteenMinuteEntryPointValue != null
                                && ltp <= fifteenMinuteEntryPointValue;
                        const ema200Cell = <td className="strategy-entry-point-cell strategy-ema200-cell"
                            title={ema200Direction === "up"
                                ? "LTP is above EMA200"
                                : ema200Direction === "down" ? "LTP is below EMA200" : "EMA200 direction unavailable"}>
                            <strong>{formatNumber(row?.ema200)}</strong>
                            {ema200Direction && <span
                                className={`strategy-ltp-arrow strategy-ltp-arrow--${ema200Direction} strategy-ltp-arrow--right`}
                                aria-label={ema200Direction === "up"
                                    ? "LTP is above EMA200" : "LTP is below EMA200"}>
                                {ema200Direction === "up" ? "↑" : "↓"}
                            </span>}
                            <span className={`strategy-ema200-distance strategy-ema200-distance--${ema200Direction ?? "neutral"}`}>
                                {ema200LtpDisplay.differenceText}{" "}
                                <span className={`strategy-ema200-percentage strategy-ema200-percentage--${ema200PercentageTone}${
                                    ema200LtpDisplay.shouldBlink ? " strategy-ema200-percentage--blink" : ""
                                }`}>
                                    ({ema200LtpDisplay.percentageText})
                                </span>
                            </span>
                        </td>;
                        const ltpCell = <td className={ltpRangeClassName}>
                            {fiveMinuteLevelReached && <span
                                className={`strategy-ltp-arrow ${highLevelReached
                                    ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"} strategy-ltp-arrow--left`}
                                title={highLevelReached ? "LTP is at or above 5-minute high" : "LTP is at or below 5-minute low"}
                                aria-label={highLevelReached ? "LTP is at or above 5-minute high" : "LTP is at or below 5-minute low"}>
                                {highLevelReached ? "↑" : "↓"}
                            </span>}
                            {formatNumber(row?.lp)}
                            {fifteenMinuteLevelReached && <span
                                className={`strategy-ltp-arrow ${highLevelReached
                                    ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"} strategy-ltp-arrow--right`}
                                title={highLevelReached ? "LTP is at or above 15-minute high" : "LTP is at or below 15-minute low"}
                                aria-label={highLevelReached ? "LTP is at or above 15-minute high" : "LTP is at or below 15-minute low"}>
                                {highLevelReached ? "↑" : "↓"}
                            </span>}
                        </td>;
                        const sectorCells = <>
                            <td>{formatSectorDisplayName(row?.sector)}</td>
                            <td className={getChangeCellClassName(sectorPercent)}>{formatNumber(sectorPercent)}%</td>
                            <td>{formatNumber(row?.avgInOI)}</td>
                        </>;
                        return <tr key={`${row?.symbol ?? title}-${index}`}>
                            <td className="strategy-symbol">{normalizeSymbol(row?.symbol) || "--"}</td>
                            {showRankHistory ? <>
                                <td>
                                    <button type="button"
                                        className="strategy-rank-history-button"
                                        title={`View rank history for ${normalizeSymbol(row?.symbol) || "stock"}`}
                                        onClick={() => setRankHistoryPopup({
                                            symbol: normalizeSymbol(row?.symbol) || "--",
                                            listTitle: title,
                                            listKey: rankListKey,
                                            isCurrentlyRanked: true,
                                            rank,
                                            rankChange,
                                            previousRank: toNumber(row?.previousRank),
                                            entryDate: row?.rankEntryDate,
                                            entryTime: row?.rankEntryTime,
                                            entryCount: row?.rankEntryCount ?? 0,
                                            ltp: row?.lp,
                                            changePercentage: row?.chp,
                                            history: rankHistory,
                                        })}>
                                        <strong>#{rank}</strong>
                                        {rankChange > 0 && <span className="strategy-rank-movement strategy-rank-movement--up">↑{rankChange}</span>}
                                        {rankChange < 0 && <span className="strategy-rank-movement strategy-rank-movement--down">↓{Math.abs(rankChange)}</span>}
                                        {rankChange === 0 && <span className="strategy-rank-movement strategy-rank-movement--same">—</span>}
                                    </button>
                                </td>
                                <td>{formatNumber(row?.firstFiveMinuteLow)}</td>
                                <td>{formatNumber(row?.firstFiveMinuteHigh)}</td>
                                <td className={getChangeCellClassName(toNumber(fiveMinuteRangePercent))}
                                    title="5-minute high-low range percentage">
                                    {formatNumber(fiveMinuteRangePercent)}%
                                </td>
                                <td className={getChangeCellClassName(toNumber(
                                    entryCalculation?.firstFifteenMinuteRangePercent ?? row?.firstFifteenMinuteRangePercent))}>
                                    {formatNumber(entryCalculation?.firstFifteenMinuteRangePercent
                                        ?? row?.firstFifteenMinuteRangePercent)}%
                                </td>
                                <td>{formatNumber(row?.firstFifteenMinuteLow)}</td>
                                <td>{formatNumber(row?.firstFifteenMinuteHigh)}</td>
                                <td className="strategy-entry-point-cell"
                                    title={entryCalculation
                                        ? `${fiveMinuteLevel} ${fiveMinuteConditionEnabled ? "enabled" : "disabled"} · ${formatNumber(fiveMinuteSlippagePercent)}% risk slippage`
                                        : "Save risk settings to load the backend entry calculation"}>
                                    <strong>{formatNumber(fiveMinuteEntryPoint)}</strong>
                                    {fiveMinuteEntryPointReached && <span
                                        className={`strategy-ltp-arrow ${highLevelReached
                                            ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"} strategy-ltp-arrow--right`}
                                        title={highLevelReached ? "LTP is at or above 5-minute high entry point" : "LTP is at or below 5-minute low entry point"}
                                        aria-label={highLevelReached ? "LTP is at or above 5-minute high entry point" : "LTP is at or below 5-minute low entry point"}>
                                        {highLevelReached ? "↑" : "↓"}
                                    </span>}
                                </td>
                                <td className="strategy-entry-point-cell"
                                    title="Calculated from 15-minute high/low and Execution Risk slippage">
                                    <strong>{formatNumber(fifteenMinuteEntryPoint)}</strong>
                                    {fifteenMinuteEntryPointReached && <span
                                        className={`strategy-ltp-arrow ${highLevelReached
                                            ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"} strategy-ltp-arrow--right`}
                                        title={highLevelReached ? "LTP is at or above 15-minute high entry point" : "LTP is at or below 15-minute low entry point"}
                                        aria-label={highLevelReached ? "LTP is at or above 15-minute high entry point" : "LTP is at or below 15-minute low entry point"}>
                                        {highLevelReached ? "↑" : "↓"}
                                    </span>}
                                </td>
                                {ema200Cell}
                                {ltpCell}
                                <td className={getChangeCellClassName(stockPercent)}>{formatNumber(stockPercent)}%</td>
                                <td className="strategy-atm-option-cell"
                                    title={row?.optionSymbol
                                        ? `${row.optionSymbol}${row?.optionExpiry ? ` · ${row.optionExpiry}` : ""}`
                                        : "ATM option is being discovered by the backend"}>
                                    <strong>{formatNumber(row?.optionStrikePrice)}</strong>
                                </td>
                                <td className={row?.optionType === "CE"
                                    ? "strategy-option-type-ce"
                                    : row?.optionType === "PE" ? "strategy-option-type-pe" : ""}>
                                    {row?.optionType ?? "--"}
                                </td>
                                <td className={optionLtpRangeClassName}>
                                    {formatNumber(optionLtpValue)}
                                    {optionLtpRangeClassName && <span
                                        className={`strategy-ltp-arrow ${optionLtpRangeClassName === "strategy-ltp-above-high"
                                            ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"}`}
                                        aria-hidden="true">
                                        {optionLtpRangeClassName === "strategy-ltp-above-high" ? "↑" : "↓"}
                                    </span>}
                                </td>
                                <td>{formatNumber(row?.optionPreviousWeekHigh)}</td>
                                <td>{formatNumber(row?.optionPreviousWeekLow)}</td>
                                <td>{row?.lotSize ?? "--"}</td>
                            </> : <>
                                <td>{formatNumber(row?.firstFiveMinuteHigh)}</td>
                                <td>{formatNumber(row?.firstFiveMinuteLow)}</td>
                                <td className={getChangeCellClassName(stockPercent)}>{formatNumber(stockPercent)}%</td>
                                {ltpCell}
                                {ema200Cell}
                                {showEntryTime && <>
                                    <td>{formatNumber(row?.optionStrikePrice)}</td>
                                    <td className={row?.optionType === "CE" ? "strategy-option-type-ce" : row?.optionType === "PE" ? "strategy-option-type-pe" : ""}>{row?.optionType ?? "--"}</td>
                                    <td className={optionLtpRangeClassName}>
                                        {formatNumber(optionLtpValue)}
                                        {optionLtpRangeClassName && <span
                                            className={`strategy-ltp-arrow ${optionLtpRangeClassName === "strategy-ltp-above-high"
                                                ? "strategy-ltp-arrow--up" : "strategy-ltp-arrow--down"}`}
                                            aria-hidden="true">
                                            {optionLtpRangeClassName === "strategy-ltp-above-high" ? "↑" : "↓"}
                                        </span>}
                                    </td>
                                    <td>{formatNumber(row?.optionPreviousWeekHigh)}</td>
                                </>}
                                {sectorCells}
                                {showEntryTime && <>
                                    <td>{row?.lotSize ?? "--"}</td>
                                    <td><button type="button" className="strategy-entry-history-link"
                                        onClick={() => setEntryHistoryPopup({
                                            symbol: normalizeSymbol(row?.symbol) || "--",
                                            side: title,
                                            history: (Array.isArray(row?.entryDateTimeHistory)
                                                ? [...row.entryDateTimeHistory] : []).reverse(),
                                        })}>
                                        {row?.localTime ?? "--"}
                                    </button></td>
                                    <td>{row?.entryCount ?? 0}</td>
                                </>}
                            </>}
                        </tr>;
                    }) : <tr><td colSpan={showRankHistory ? 19 : 9 + (showEntryTime ? 7 : 0)} className="stock-scanner-empty-cell">Waiting for {title.toLowerCase()} data...</td></tr>}</tbody>
                </table>
            </div>
        </section>
        );
    };

    const renderStrategySectorTable = (title, rows, tone = "neutral") => (
        <section className={`strategy-table-card strategy-table-card--${tone}`}>
            <div className="strategy-table-title">{title} ({rows.length})</div>
            <div className="strategy-table-scroll">
                <table className="responsive-table table-bordered text-center strategy-table">
                    <thead><tr><th>SECTOR</th><th>LTP</th><th>CHANGE</th><th>CHANGE %</th></tr></thead>
                    <tbody>{rows.length ? rows.map((row, index) => {
                        const percent = toNumber(row?.chp);
                        return <tr key={`${row?.symbol ?? title}-${index}`}>
                            <td className="strategy-symbol">{formatSectorDisplayName(getScannerHeaderSymbol(row?.symbol))}</td>
                            <td>{formatNumber(row?.lp)}</td>
                            <td className={getChangeCellClassName(row?.ch)}>{formatNumber(row?.ch)}</td>
                            <td className={getChangeCellClassName(percent)}>{formatNumber(percent)}%</td>
                        </tr>;
                    }) : <tr><td colSpan="4" className="stock-scanner-empty-cell">Waiting for {title.toLowerCase()} data...</td></tr>}</tbody>
                </table>
            </div>
        </section>
    );

    const renderOiSpurtsTable = (rows) => (
        <section className="strategy-table-card strategy-table-card--oi strategy-table-card--wide">
            <div className="strategy-table-title">TOP 25 OI SPURTS ({rows.length})</div>
            <div className="strategy-table-scroll">
                <table className="responsive-table table-bordered text-center strategy-table">
                    <thead><tr><th>SYMBOL</th><th>AVG OI %</th><th>LATEST OI</th><th>PREVIOUS OI</th><th>CHANGE IN OI</th><th>VOLUME</th><th>UNDERLYING</th></tr></thead>
                    <tbody>{rows.length ? rows.map((row, index) => <tr key={`${row?.symbol ?? "oi"}-${index}`}>
                        <td className="strategy-symbol">{row?.symbol ?? "--"}</td>
                        <td>{formatNumber(row?.avgInOI)}</td><td>{formatNumber(row?.latestOI)}</td>
                        <td>{formatNumber(row?.prevOI)}</td><td>{formatNumber(row?.changeInOI)}</td>
                        <td>{formatNumber(row?.volume)}</td><td>{formatNumber(row?.underlyingValue)}</td>
                    </tr>) : <tr><td colSpan="7" className="stock-scanner-empty-cell">Waiting for OI spurts data...</td></tr>}</tbody>
                </table>
            </div>
        </section>
    );

    const renderExecutionAuditDetails = (trade) => {
        const risk = trade?.executionSettingsSnapshot ?? {};
        const configuredStock = trade?.stockConfigurationSnapshot ?? {};
        const signalPriceField = trade?.signalPriceField ??
            (trade?.signalSide === "SELL" ? "PELTP" : "CELTP");
        const premium = trade?.signalPrice ??
            (signalPriceField === "PELTP" ? trade?.peLtp : trade?.ceLtp);
        const brokerStatus = trade?.brokerExitStatus ?? trade?.brokerEntryStatus;
        const brokerMessage = trade?.brokerExitMessage ?? trade?.brokerEntryMessage;
        const quoteTimestamp = toNumber(trade?.quoteTimestampEpochSeconds);
        const quoteAge = quoteTimestamp == null ? null
            : Math.max(0, Math.floor(Date.now() / 1000 - quoteTimestamp));
        const quoteDateTime = quoteTimestamp == null ? "--"
            : new Date(quoteTimestamp * 1000).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata", hour12: false
            });
        const displayedBrokerStatus = trade?.lastError === "ENTRY_PENDING_TIMEOUT_CANCEL_REQUESTED"
            ? "CANCEL_PENDING" : brokerStatus;
        const formatAuditPercent = (value) => toNumber(value) == null
            ? "--" : `${formatNumber(value)}%`;
        const formatAuditCurrency = (value) => toNumber(value) == null
            ? "--" : `₹${formatNumber(value)}`;
        const auditItem = (label, value, className = "") => <div
            className="buy-sell-execution-audit-item" key={label}>
            <span>{label}</span>
            <strong className={className}>{value ?? "--"}</strong>
        </div>;

        return <div className="buy-sell-execution-audit">
            <section>
                <h4>SIGNAL SNAPSHOT</h4>
                <div className="buy-sell-execution-audit-grid">
                    {auditItem("Signal Date/Time", [trade?.signalDate, trade?.signalTime]
                        .filter(Boolean).join(" ") || "--")}
                    {auditItem("Trigger", signalPriceField)}
                    {auditItem("Premium", formatNumber(premium))}
                    {auditItem("Entry Level", trade?.entryLevel ?? risk?.entryLevel ?? "WH")}
                    {auditItem("Matched Levels", Array.isArray(trade?.matchedEntryLevels)
                        ? trade.matchedEntryLevels.join(", ") : trade?.entryLevel)}
                    {auditItem("Reference", formatNumber(
                        trade?.entryReferencePrice ?? trade?.previousWeekHigh
                    ))}
                    {auditItem("Observed", formatNumber(
                        trade?.entryObservedPrice ?? premium
                    ))}
                    {auditItem("Band Limit", formatNumber(
                        trade?.entryBoundaryPrice ?? trade?.maximumEntryPrice
                    ))}
                    {auditItem("Spot Price", formatNumber(trade?.spotPrice ?? trade?.stockLtp))}
                    {auditItem("Quote Time", quoteDateTime)}
                    {auditItem("Quote Age", quoteAge == null ? "--" : `${quoteAge}s`)}
                    {auditItem("CELTP", formatNumber(trade?.ceLtp))}
                    {auditItem("PELTP", formatNumber(trade?.peLtp))}
                    {auditItem("5MH", formatNumber(trade?.firstFiveMinuteHigh))}
                    {auditItem("5ML", formatNumber(trade?.firstFiveMinuteLow))}
                    {auditItem("Stock %", formatAuditPercent(trade?.stockPercent),
                        getChangeCellClassName(trade?.stockPercent))}
                    {auditItem("Sector", trade?.sector)}
                    {auditItem("Sector %", formatAuditPercent(trade?.sectorPercent),
                        getChangeCellClassName(trade?.sectorPercent))}
                    {auditItem("AVG OI %", formatAuditPercent(trade?.avgInOI))}
                    {auditItem("Entry Count", trade?.signalEntryCount ?? "--")}
                    {auditItem("Entry Rule", trade?.entryReason)}
                </div>
            </section>
            <section>
                <h4>EXECUTION &amp; RISK SNAPSHOT</h4>
                <div className="buy-sell-execution-audit-grid">
                    {auditItem("Mode", trade?.tradingMode)}
                    {auditItem("Product", risk?.productType ?? trade?.productType)}
                    {auditItem("Order Type", risk?.orderType ?? trade?.orderType)}
                    {auditItem("Default Lots", risk?.defaultLots ?? trade?.lots)}
                    {auditItem("Quantity", trade?.quantity)}
                    {auditItem("Entry Window", risk?.startTime && risk?.endTime
                        ? `${risk.startTime} - ${risk.endTime}` : "--")}
                    {auditItem("Square Off", risk?.squareOffTime)}
                    {auditItem("Stop Loss / Trade", formatAuditCurrency(risk?.stopLoss))}
                    {auditItem("Target / Trade", formatAuditCurrency(risk?.target))}
                    {auditItem("Max Daily Loss", formatAuditCurrency(risk?.maximumDailyLoss))}
                    {auditItem("Max Trades", risk?.maximumTradesPerDay)}
                    {auditItem("Max Open Positions", risk?.maximumOpenPositions)}
                    {auditItem("Max Order Value", formatAuditCurrency(risk?.maximumOrderValue))}
                    {auditItem("Entry Conditions", Array.isArray(risk?.entryConditions)
                        ? risk.entryConditions
                            .filter((condition) => condition?.enabled)
                            .map((condition) => `${condition.level} ${formatAuditPercent(
                                condition.slippagePercent
                            )}`).join(" · ")
                        : "--")}
                    {auditItem("Trigger Slippage", formatAuditPercent(
                        trade?.entrySlippagePercent ?? risk?.maximumEntryDistancePercent
                    ))}
                    {auditItem("Order Slippage", formatAuditPercent(risk?.maximumSlippagePercent))}
                    {auditItem("One Trade/Symbol", risk?.oneTradePerSymbolPerDay == null
                        ? "--" : risk.oneTradePerSymbolPerDay ? "YES" : "NO")}
                </div>
            </section>
            <section>
                <h4>CONTRACT &amp; BROKER AUDIT</h4>
                <div className="buy-sell-execution-audit-grid">
                    {auditItem("Premium Symbol", trade?.optionPremiumSymbol ?? trade?.optionSymbol)}
                    {auditItem("Strike Rule", configuredStock?.strikeRule)}
                    {auditItem("Expiry Rule", configuredStock?.expiryRule)}
                    {auditItem("Expiry", trade?.optionExpiry)}
                    {auditItem("Lot Size", trade?.lotSize)}
                    {auditItem("Broker", trade?.brokerType ??
                        (trade?.tradingMode === "PAPER" ? "PAPER ENGINE" : "--"))}
                    {auditItem("Broker Status", displayedBrokerStatus)}
                    {auditItem("Broker Message", brokerMessage)}
                    {auditItem("Trading Symbol", trade?.brokerTradingSymbol)}
                    {auditItem("Security ID", trade?.brokerSecurityId)}
                    {auditItem("Exchange Segment", trade?.brokerExchangeSegment)}
                    {auditItem("Entry Order ID", trade?.brokerEntryOrderId)}
                    {auditItem("Exit Order ID", trade?.brokerExitOrderId)}
                    {auditItem("Last Error", trade?.lastError, "negative")}
                </div>
            </section>
        </div>;
    };

    const renderRunningPositions = (trades) => (
        <section className="buy-sell-running-card">
            <div className="buy-sell-running-header">
                <div>
                    <strong>RUNNING PAPER / LIVE POSITIONS ({trades.length})</strong>
                    <span>Active backend executions with current premium, target, stop-loss and P&amp;L</span>
                </div>
                <span className={executionSocketConnected ? "connected" : "polling"}>
                    {executionSocketConnected ? "WS CONNECTED" : "REST POLLING · 5 SEC"}
                </span>
            </div>
            <div className="buy-sell-running-scroll">
                <table className="responsive-table table-bordered text-center buy-sell-running-table">
                    <thead><tr>
                        <th>MODE</th><th>SIDE</th><th>SYMBOL</th><th>SPOT</th><th>STRIKE</th>
                        <th>PREMIUM SYMBOL</th><th>TYPE</th>
                        <th>LOTS</th><th>QTY</th>
                        <th>ENTRY PRICE</th><th>ENTRY</th><th>CURRENT</th><th>TGT</th><th>SL</th>
                        <th>P&amp;L</th>
                        <th>ENTRY DATE</th><th>ENTRY TIME</th><th>STATUS</th><th>ACTION</th>
                    </tr></thead>
                    <tbody>{trades.length ? trades.map((trade) => {
                        const tradeKey = trade?.id ?? trade?.signalKey;
                        const detailsExpanded = expandedExecutionTradeId === tradeKey;
                        const pnl = toNumber(trade?.unrealizedPnl);
                        const premiumSymbol = trade?.optionPremiumSymbol ?? trade?.optionSymbol;
                        const verifyingBroker = isBrokerFlatConfirmationPending(trade);
                        return <React.Fragment key={`running-${tradeKey}`}>
                            <tr>
                                <td><span className={`buy-sell-mode-chip buy-sell-mode-chip--${String(
                                    trade?.tradingMode ?? "unknown"
                                ).toLowerCase()}`}>{trade?.tradingMode ?? "--"}</span></td>
                                <td className={trade?.signalSide === "BUY"
                                    ? "positive" : "negative"}>{trade?.signalSide ?? "--"}</td>
                                <td className="strategy-symbol" title={trade?.symbol ?? undefined}>
                                    {normalizeSymbol(trade?.symbol) || "--"}
                                </td>
                                <td>{formatNumber(trade?.spotPrice ?? trade?.stockLtp)}</td>
                                <td>{formatNumber(trade?.optionStrikePrice)}</td>
                                <td className="buy-sell-premium-symbol" title={premiumSymbol}>
                                    {premiumSymbol ?? "--"}
                                </td>
                                <td className={trade?.optionType === "CE"
                                    ? "strategy-option-type-ce"
                                    : trade?.optionType === "PE"
                                        ? "strategy-option-type-pe" : ""}>
                                    {trade?.optionType ?? "--"}
                                </td>
                                <td>{trade?.lots ?? "--"}</td>
                                <td>{trade?.quantity ?? "--"}</td>
                                <td>{formatNumber(trade?.entryPrice)}</td>
                                <td>{formatNumber(trade?.previousWeekHigh)}</td>
                                <td>{formatNumber(trade?.lastPrice)}</td>
                                <td>{formatNumber(getTradeTarget(trade))}</td>
                                <td>{formatNumber(getTradeStopLoss(trade))}</td>
                                <td className={getChangeCellClassName(pnl)}>₹{formatNumber(pnl)}</td>
                                <td>{trade?.createdDate ?? trade?.signalDate ?? "--"}</td>
                                <td>{trade?.createdTime ?? trade?.signalTime ?? "--"}</td>
                                <td><span className={`buy-sell-trade-status buy-sell-trade-status--${
                                    getExecutionStatusClass(trade)
                                }`}>{getExecutionStatusLabel(trade)}</span></td>
                                <td><div className="buy-sell-execution-actions">
                                    <button type="button" className="buy-sell-details-button"
                                        aria-expanded={detailsExpanded}
                                        onClick={() => setExpandedExecutionTradeId(
                                            detailsExpanded ? "" : tradeKey
                                        )}>
                                        {detailsExpanded ? "HIDE" : "DETAILS"}
                                    </button>
                                    {trade?.status === "OPEN" && <button type="button"
                                        className="buy-sell-square-off-button"
                                        disabled={Boolean(buySellTradeActionId) || verifyingBroker}
                                        onClick={() => handleSquareOffTrade(trade)}>
                                        {verifyingBroker ? "VERIFYING..."
                                            : buySellTradeActionId === trade.id
                                                ? "SUBMITTING..." : "SQUARE OFF"}
                                    </button>}
                                </div></td>
                            </tr>
                            {detailsExpanded && <tr className="buy-sell-execution-detail-row">
                                <td colSpan="19">{renderExecutionAuditDetails(trade)}</td>
                            </tr>}
                        </React.Fragment>;
                    }) : <tr><td colSpan="19" className="stock-scanner-empty-cell">
                        No PAPER or LIVE positions are currently running.
                    </td></tr>}</tbody>
                </table>
            </div>
        </section>
    );

    const renderBuySellExecutions = ({
        trades = buySellTrades,
        loading = buySellTradesLoading,
        emptyMessage = "No executions are available for today.",
        historyView = false,
    } = {}) => {
        if (!historyView && !buySellStrategyId) {
            return <div className="buy-sell-execution-empty">
                Save the strategy to view paper or live executions.
            </div>;
        }

        return <div className="buy-sell-execution-scroll">
            <table className="responsive-table table-bordered text-center buy-sell-execution-table">
                <thead><tr>
                    <th>MODE</th>
                    <th>SIGNAL</th><th>SYMBOL</th><th>STRIKE</th>
                    <th>TYPE</th><th>PREMIUM SYMBOL</th><th>SPOT</th>
                    <th>RULE</th><th>LOTS</th><th>QTY</th><th>STATUS</th>
                    <th>ENTRY</th><th>CURRENT</th><th>TGT</th><th>SL</th><th>EXIT</th><th>P&amp;L</th>
                    <th>ENTRY TIME</th><th>EXIT TIME</th><th>EXIT REASON</th>
                    <th>BROKER STATUS</th><th>ORDER ID</th><th>ERROR</th><th>ACTION</th>
                </tr></thead>
                <tbody>
                    {loading ? <tr>
                        <td colSpan="24" className="stock-scanner-empty-cell">
                            Loading {historyView ? "execution history" : "today's executions"}...
                        </td>
                    </tr> : trades.length ? trades.map((trade) => {
                        const pnl = trade?.status === "CLOSED"
                            ? toNumber(trade?.realizedPnl)
                            : toNumber(trade?.unrealizedPnl);
                        const orderId = trade?.brokerExitOrderId ??
                            trade?.brokerEntryOrderId ?? "--";
                        const signalPriceField = trade?.signalPriceField ??
                            (trade?.signalSide === "SELL" ? "PELTP" : "CELTP");
                        const premium = toNumber(trade?.signalPrice ??
                            (signalPriceField === "PELTP" ? trade?.peLtp : trade?.ceLtp));
                        const premiumSymbol = trade?.optionPremiumSymbol ?? trade?.optionSymbol;
                        const entryLevel = String(trade?.entryLevel ?? "WH").toUpperCase();
                        const entryReferencePrice = toNumber(trade?.entryReferencePrice) ??
                            (entryLevel === "WH" ? toNumber(trade?.previousWeekHigh)
                                : entryLevel === "WL" ? toNumber(trade?.previousWeekLow)
                                    : entryLevel === "5MH" ? toNumber(trade?.firstFiveMinuteHigh)
                                        : entryLevel === "5ML" ? toNumber(trade?.firstFiveMinuteLow)
                                            : entryLevel === "15MH" ? toNumber(trade?.firstFifteenMinuteHigh)
                                                : entryLevel === "15ML" ? toNumber(trade?.firstFifteenMinuteLow) : null);
                        const entryObservedPrice = toNumber(trade?.entryObservedPrice) ?? premium;
                        const entryBoundaryPrice = toNumber(trade?.entryBoundaryPrice);
                        const ruleEvaluated = entryObservedPrice != null && entryReferencePrice != null;
                        const ruleMet = ruleEvaluated && (entryLevel.endsWith("H")
                            ? entryObservedPrice >= entryReferencePrice &&
                                (entryBoundaryPrice == null || entryObservedPrice <= entryBoundaryPrice)
                            : entryObservedPrice <= entryReferencePrice &&
                                (entryBoundaryPrice == null || entryObservedPrice >= entryBoundaryPrice));
                        const brokerStatus = trade?.brokerExitStatus ??
                            trade?.brokerEntryStatus ??
                            (trade?.tradingMode === "PAPER" ? "PAPER" : "--");
                        const displayedBrokerStatus = trade?.lastError === "ENTRY_PENDING_TIMEOUT_CANCEL_REQUESTED"
                            ? "CANCEL_PENDING" : brokerStatus;
                        const brokerMessage = trade?.brokerExitMessage ??
                            trade?.brokerEntryMessage;
                        const errorMessage = trade?.lastError ??
                            (["FAILED", "REJECTED"].includes(trade?.status)
                                ? brokerMessage : null) ?? "--";
                        const tradeKey = trade?.id ?? trade?.signalKey;
                        const detailsExpanded = expandedExecutionTradeId === tradeKey;

                        return <React.Fragment key={tradeKey}>
                            <tr>
                                <td>{trade?.tradingMode ?? "--"}</td>
                                <td className={trade?.signalSide === "BUY"
                                    ? "positive" : "negative"}>{trade?.signalSide ?? "--"}</td>
                                <td className="strategy-symbol" title={trade?.symbol ?? undefined}>
                                    {normalizeSymbol(trade?.symbol) || "--"}
                                </td>
                                <td>{formatNumber(trade?.optionStrikePrice)}</td>
                                <td>{trade?.optionType ?? "--"}</td>
                                <td className="buy-sell-premium-symbol" title={premiumSymbol}>
                                    {premiumSymbol ?? "--"}
                                </td>
                                <td title={`${entryLevel} reference ${formatNumber(entryReferencePrice)} · boundary ${formatNumber(entryBoundaryPrice)}`}>
                                    {formatNumber(entryReferencePrice)}
                                </td>
                                <td><span className={`buy-sell-trigger-rule ${ruleEvaluated
                                    ? ruleMet
                                        ? "buy-sell-trigger-rule--met"
                                        : "buy-sell-trigger-rule--not-met"
                                    : "buy-sell-trigger-rule--unknown"}`}>
                                    {ruleEvaluated ? ruleMet ? "MET" : "NOT MET" : "--"}
                                </span></td>
                                <td>{trade?.lots ?? "--"}</td>
                                <td>{trade?.quantity ?? "--"}</td>
                                <td><span className={`buy-sell-trade-status buy-sell-trade-status--${
                                    getExecutionStatusClass(trade)
                                }`}>{getExecutionStatusLabel(trade)}</span></td>
                                <td>{formatNumber(trade?.entryPrice)}</td>
                                <td>{formatNumber(trade?.lastPrice)}</td>
                                <td>{formatNumber(getTradeTarget(trade))}</td>
                                <td>{formatNumber(getTradeStopLoss(trade))}</td>
                                <td>{formatNumber(trade?.exitPrice)}</td>
                                <td className={getChangeCellClassName(pnl)}>{formatNumber(pnl)}</td>
                                <td>{formatExecutionDateTime(trade?.entryAt ?? trade?.entryRequestedAt)}</td>
                                <td>{formatExecutionDateTime(trade?.exitAt ?? trade?.exitRequestedAt)}</td>
                                <td>{trade?.exitReason ?? "--"}</td>
                                <td><span className={`buy-sell-broker-status buy-sell-broker-status--${String(
                                    displayedBrokerStatus
                                ).toLowerCase().replace(/[^a-z0-9]+/g, "_")}`}>{displayedBrokerStatus}</span></td>
                                <td className="buy-sell-order-id">{orderId}</td>
                                <td className="buy-sell-trade-error" title={errorMessage}>{errorMessage}</td>
                                <td><div className="buy-sell-execution-actions">
                                    <button type="button" className="buy-sell-details-button"
                                        aria-expanded={detailsExpanded}
                                        onClick={() => setExpandedExecutionTradeId(
                                            detailsExpanded ? "" : tradeKey
                                        )}>
                                        {detailsExpanded ? "HIDE" : "DETAILS"}
                                    </button>
                                    {!historyView && trade?.status === "OPEN" && <button type="button"
                                        className="buy-sell-square-off-button"
                                        disabled={Boolean(buySellTradeActionId) ||
                                            isBrokerFlatConfirmationPending(trade)}
                                        onClick={() => handleSquareOffTrade(trade)}>
                                        {isBrokerFlatConfirmationPending(trade)
                                            ? "VERIFYING..."
                                            : buySellTradeActionId === trade.id
                                                ? "SUBMITTING..." : "SQUARE OFF"}
                                    </button>}
                                </div></td>
                            </tr>
                            {detailsExpanded && <tr className="buy-sell-execution-detail-row">
                                <td colSpan="24">{renderExecutionAuditDetails(trade)}</td>
                            </tr>}
                        </React.Fragment>;
                    }) : <tr>
                        <td colSpan="24" className="stock-scanner-empty-cell">
                            {emptyMessage}
                        </td>
                    </tr>}
                </tbody>
            </table>
        </div>;
    };

    const renderBuySellExecutionHistory = () => {
        const maxHistoryDate = getMarketDateString();
        const updateStartDate = (event) => {
            const nextDate = event.target.value;
            setExecutionHistoryStartDate(nextDate);
            if (executionHistoryEndDate && nextDate > executionHistoryEndDate) {
                setExecutionHistoryEndDate(nextDate);
            }
        };
        const updateEndDate = (event) => {
            const nextDate = event.target.value;
            setExecutionHistoryEndDate(nextDate);
            if (executionHistoryStartDate && nextDate < executionHistoryStartDate) {
                setExecutionHistoryStartDate(nextDate);
            }
        };
        const closedHistoryTrades = executionHistoryTrades
            .filter((trade) => trade?.status === "CLOSED");
        const historyProfit = closedHistoryTrades
            .reduce((total, trade) => {
                const realizedPnl = toNumber(trade?.realizedPnl) ?? 0;
                return total + (realizedPnl > 0 ? realizedPnl : 0);
            }, 0);
        const historyLoss = closedHistoryTrades
            .reduce((total, trade) => {
                const realizedPnl = toNumber(trade?.realizedPnl) ?? 0;
                return total + (realizedPnl < 0 ? realizedPnl : 0);
            }, 0);
        const historyBookedPnl = historyProfit + historyLoss;
        const historyInvestmentAmount = executionHistoryTrades
            .reduce((total, trade) => total + getTradeInvestmentAmount(trade), 0);

        return <div className="buy-sell-history-panel">
            <div className="buy-sell-history-filter">
                <div className="buy-sell-history-date-range">
                    <label className="buy-sell-history-date-control">
                        <span>START DATE</span>
                        <input
                            type="date"
                            value={executionHistoryStartDate}
                            onChange={updateStartDate}
                            max={executionHistoryEndDate || maxHistoryDate}
                            aria-label="Execution history start date"
                        />
                    </label>
                    <label className="buy-sell-history-date-control">
                        <span>END DATE</span>
                        <input
                            type="date"
                            value={executionHistoryEndDate}
                            onChange={updateEndDate}
                            min={executionHistoryStartDate || undefined}
                            max={maxHistoryDate}
                            aria-label="Execution history end date"
                        />
                    </label>
                </div>
                <div className="buy-sell-history-result">
                    <span>RESULT</span>
                    <strong>{executionHistoryLoading
                        ? "LOADING..."
                        : `${executionHistoryTrades.length} EXECUTION${executionHistoryTrades.length === 1 ? "" : "S"}`}</strong>
                </div>
                <div className="buy-sell-history-result buy-sell-history-profit">
                    <span>PROFIT</span>
                    <strong className={executionHistoryLoading ? "" : "positive"}>
                        {executionHistoryLoading ? "LOADING..." : `₹${formatNumber(historyProfit)}`}
                    </strong>
                </div>
                <div className="buy-sell-history-result buy-sell-history-loss">
                    <span>LOSS</span>
                    <strong className={executionHistoryLoading ? "" : "negative"}>
                        {executionHistoryLoading ? "LOADING..." : `₹${formatNumber(historyLoss)}`}
                    </strong>
                </div>
                <div className="buy-sell-history-result buy-sell-history-booked-pnl">
                    <span>BOOKED P&amp;L</span>
                    <strong className={executionHistoryLoading
                        ? "" : getChangeCellClassName(historyBookedPnl)}>
                        {executionHistoryLoading
                            ? "LOADING..."
                            : `₹${formatNumber(historyBookedPnl)}`}
                    </strong>
                </div>
                <div className="buy-sell-history-result buy-sell-history-investment">
                    <span>INVESTMENT AMOUNT</span>
                    <strong>
                        {executionHistoryLoading
                            ? "LOADING..."
                            : `₹${formatNumber(historyInvestmentAmount)}`}
                    </strong>
                </div>
            </div>
            {executionHistoryError && <div className="buy-sell-history-error" role="alert">
                {executionHistoryError}
            </div>}
            {renderBuySellExecutions({
                trades: executionHistoryTrades,
                loading: executionHistoryLoading,
                emptyMessage: executionHistoryStartDate && executionHistoryEndDate
                    ? `No executions were found from ${executionHistoryStartDate} to ${executionHistoryEndDate}.`
                    : "Select a start date and end date to view history.",
                historyView: true,
            })}
        </div>;
    };

    const renderBuySellStrategySetup = () => {
        const selectedBroker = buySellBrokers.find(
            (broker) => String(getBrokerId(broker)) === selectedBuySellBrokerId
        );
        const executionSettingsValid = isBuySellExecutionSettingsValid();
        const canSaveStrategy = Boolean(user?.userId) && executionSettingsValid;
        const canActivatePaper = Boolean(user?.userId) && executionSettingsValid &&
            buySellExecutionCapabilities.paperExecutionEnabled;
        const canActivateLive = Boolean(user?.userId) && executionSettingsValid &&
            Boolean(selectedBroker) && isBrokerReady(selectedBroker) &&
            buySellExecutionCapabilities.liveExecutionEnabled;
        const hasOpenTrades = buySellTrades.some((trade) => trade?.status === "OPEN");
        const enabledEntryLevels = (buySellExecutionSettings.entryConditions ?? [])
            .filter((condition) => condition.enabled)
            .map((condition) => condition.level);
        const entryConditionByLevel = new Map(
            (buySellExecutionSettings.entryConditions ?? []).map(
                (condition) => [condition.level, condition]
            )
        );
        const fiveMinuteSlippage = ["5MH", "5ML"]
            .map((level) => entryConditionByLevel.get(level))
            .find((condition) => condition?.enabled)?.slippagePercent ??
            entryConditionByLevel.get("5MH")?.slippagePercent ?? "1.5";
        const ema200Slippage = entryConditionByLevel.get("EMA200")?.slippagePercent ?? "1.5";
        const weekSlippage = ["WH", "WL"]
            .map((level) => entryConditionByLevel.get(level))
            .find((condition) => condition?.enabled)?.slippagePercent ??
            entryConditionByLevel.get("WH")?.slippagePercent ?? "1.5";
        const statusLabel = buySellStrategyStatus === "ACTIVE" &&
            buySellActivatedTradingMode
            ? `ACTIVE · ${buySellActivatedTradingMode}`
            : buySellStrategyStatus;

        return (
            <section className="buy-sell-strategy-control-card">
                <div className="buy-sell-strategy-control-header">
                    <div className="buy-sell-strategy-tabs" role="tablist" aria-label="Strategy configuration">
                        <button type="button" role="tab"
                            aria-selected={buySellSettingsTab === "EXECUTION_RISK"}
                            className={buySellSettingsTab === "EXECUTION_RISK" ? "active" : ""}
                            onClick={() => setBuySellSettingsTab("EXECUTION_RISK")}>
                            RISK SETTINGS
                        </button>
                        <button type="button" role="tab"
                            aria-selected={buySellSettingsTab === "EXECUTIONS"}
                            className={buySellSettingsTab === "EXECUTIONS" ? "active" : ""}
                            onClick={() => setBuySellSettingsTab("EXECUTIONS")}>
                            EXECUTIONS ({buySellTrades.length})
                        </button>
                        <button type="button" role="tab"
                            aria-selected={buySellSettingsTab === "HISTORY"}
                            className={buySellSettingsTab === "HISTORY" ? "active" : ""}
                            onClick={() => setBuySellSettingsTab("HISTORY")}>
                            HISTORY
                        </button>
                    </div>
                    <div className="buy-sell-strategy-lifecycle">
                        <span className={`buy-sell-strategy-status buy-sell-strategy-status--${buySellStrategyStatus.toLowerCase()}`}>
                            {statusLabel}
                        </span>
                        <button type="button" onClick={() => saveBuySellStrategy({
                            status: buySellStrategyId ? "PAUSED" : "DRAFT",
                            autoExecutionEnabled: false,
                            successMessage: "Risk settings saved. Choose ACTIVATE PAPER or ACTIVATE LIVE to start execution.",
                        })} disabled={!canSaveStrategy || buySellStrategySaving}>SAVE RISK</button>
                        <button type="button" className="paper"
                            onClick={() => activateBuySellStrategy("PAPER")}
                            disabled={!canActivatePaper || buySellStrategySaving}>ACTIVATE PAPER</button>
                        <button type="button" className="live"
                            onClick={() => activateBuySellStrategy("LIVE")}
                            disabled={!canActivateLive || buySellStrategySaving}
                            title={buySellExecutionCapabilities.liveExecutionEnabled
                                ? "Activate real broker execution"
                                : "LIVE execution is disabled by stock-api"}>ACTIVATE LIVE</button>
                        <button type="button" onClick={() => updateBuySellStrategyLifecycle("PAUSED")}
                            disabled={buySellStrategyStatus !== "ACTIVE" || buySellStrategySaving}>PAUSE</button>
                        <button type="button" className="stop"
                            onClick={() => updateBuySellStrategyLifecycle("STOPPED")}
                            disabled={!['ACTIVE', 'PAUSED'].includes(buySellStrategyStatus) || buySellStrategySaving}>STOP</button>
                        <button type="button" className="stop"
                            onClick={handleSquareOffStrategy}
                            disabled={!hasOpenTrades || Boolean(buySellTradeActionId)}>
                            {buySellTradeActionId === "ALL" ? "SUBMITTING..." : "SQUARE OFF ALL"}
                        </button>
                    </div>
                </div>

                {buySellSettingsTab === "EXECUTION_RISK" && (
                    <>
                    <div className="buy-sell-auto-execution-note">
                        <div>
                            <strong>AUTOMATIC SCHEDULER SIGNALS</strong>
                            <span>Top 20 Gainers are BUY candidates and Top 20 Losers are SELL candidates. ANY enabled condition can trigger one order after its own retest confirmation. EMA200 requires the open/LTP side rules and the configured EMA200 distance percentage. Enabled: {enabledEntryLevels.join(", ") || "none"}. No browser stock selection is required.</span>
                        </div>
                        <div className={selectedBroker && isBrokerReady(selectedBroker)
                            ? "connected" : "disconnected"}>
                            <strong>{buySellBrokerLoading
                                ? "CHECKING BROKER"
                                : selectedBroker
                                    ? selectedBroker?.accountName ?? selectedBroker?.brokerType
                                    : "NO CONNECTED BROKER"}</strong>
                            <span>{buySellBrokerLoadError
                                ? "Broker accounts could not be loaded"
                                : selectedBroker && isBrokerReady(selectedBroker)
                                    ? "Connected from Broker page · available for LIVE"
                                    : "Connect a broker on the Broker page for LIVE execution"}</span>
                        </div>
                    </div>
                    <div className="buy-sell-risk-grid">
                        <label className="buy-sell-strategy-field"><span>Trading Mode</span><select value={buySellExecutionSettings.tradingMode} onChange={(event) => updateBuySellExecutionSetting("tradingMode", event.target.value)}><option value="PAPER">PAPER</option><option value="LIVE">LIVE</option></select></label>
                        <label className="buy-sell-strategy-field"><span>Product</span><select value={buySellExecutionSettings.productType} onChange={(event) => updateBuySellExecutionSetting("productType", event.target.value)}><option value="INTRADAY">INTRADAY</option><option value="NRML">NRML</option></select></label>
                        <label className="buy-sell-strategy-field"><span>Order Type</span><select value={buySellExecutionSettings.orderType} onChange={(event) => updateBuySellExecutionSetting("orderType", event.target.value)}><option value="MARKET">MARKET</option><option value="LIMIT">LIMIT</option></select></label>
                        <label className="buy-sell-strategy-field"><span>Default Lots</span><input type="number" min="1" step="1" value={buySellExecutionSettings.defaultLots} onChange={(event) => updateBuySellExecutionSetting("defaultLots", event.target.value)} /></label>
                        <div className="buy-sell-entry-condition-row">
                        <div className="buy-sell-strategy-field buy-sell-entry-condition-selector buy-sell-entry-condition-selector--stock">
                            <span>Entry Condition (LTP)</span>
                            <div className="buy-sell-risk-checks buy-sell-entry-condition-box">
                                <div className="buy-sell-entry-condition-toggles">
                                    {["EMA200", "5MH", "5ML", "15MH", "15ML"].map((level) => <label key={`entry-toggle-${level}`}>
                                        <input type="checkbox" checked={Boolean(entryConditionByLevel.get(level)?.enabled)}
                                            onChange={(event) => updateBuySellEntryCondition(level, "enabled", event.target.checked)} />
                                        {level}
                                    </label>)}
                                </div>
                                <div className="buy-sell-entry-group-slippage buy-sell-entry-group-slippage--ema200">
                                    <span>EMA200 (%)</span>
                                    <div className="buy-sell-entry-slippage-stepper">
                                        <button type="button" aria-label="Decrease EMA200 distance percentage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["EMA200"], ema200Slippage, -0.1
                                            )}>−</button>
                                        <input type="number" min="0.01" max="100" step="0.1"
                                            aria-label="EMA200 distance percentage"
                                            value={ema200Slippage}
                                            onChange={(event) => updateBuySellEntryConditionGroupSlippage(
                                                ["EMA200"], event.target.value
                                            )} />
                                        <button type="button" aria-label="Increase EMA200 distance percentage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["EMA200"], ema200Slippage, 0.1
                                            )}>+</button>
                                    </div>
                                    <small>%</small>
                                </div>
                                <div className="buy-sell-entry-group-slippage">
                                    <span>Slippage (5M / 15M)</span>
                                    <div className="buy-sell-entry-slippage-stepper">
                                        <button type="button" aria-label="Decrease 5-minute slippage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["5MH", "5ML", "15MH", "15ML"], fiveMinuteSlippage, -0.1
                                            )}>−</button>
                                        <input type="number" min="0.01" max="100" step="0.1"
                                            aria-label="5-minute slippage percentage"
                                            value={fiveMinuteSlippage}
                                            onChange={(event) => updateBuySellEntryConditionGroupSlippage(
                                                ["5MH", "5ML", "15MH", "15ML"], event.target.value
                                            )} />
                                        <button type="button" aria-label="Increase 5-minute slippage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["5MH", "5ML", "15MH", "15ML"], fiveMinuteSlippage, 0.1
                                            )}>+</button>
                                    </div>
                                    <small>%</small>
                                </div>
                            </div>
                        </div>
                        <div className="buy-sell-strategy-field buy-sell-entry-condition-selector buy-sell-entry-condition-selector--option">
                            <span>5/15M HL (%)</span>
                            <div className="buy-sell-risk-checks buy-sell-entry-condition-box">
                                <div className="buy-sell-entry-condition-toggles">
                                    <label>
                                        <input type="checkbox"
                                            checked={buySellExecutionSettings.fiveMinuteRangeEnabled !== false}
                                            onChange={(event) => updateBuySellExecutionSetting(
                                                "fiveMinuteRangeEnabled", event.target.checked
                                            )} />
                                        5MHL
                                    </label>
                                    <label>
                                        <input type="checkbox"
                                            checked={buySellExecutionSettings.fifteenMinuteRangeEnabled !== false}
                                            onChange={(event) => updateBuySellExecutionSetting(
                                                "fifteenMinuteRangeEnabled", event.target.checked
                                            )} />
                                        15MHL
                                    </label>
                                </div>
                                <div className="buy-sell-entry-group-slippage">
                                    <div className="buy-sell-entry-slippage-stepper">
                                        <button type="button" aria-label="Decrease 5/15-minute high-low range percentage"
                                            onClick={() => adjustBuySellIntradayRangePercent(
                                                buySellExecutionSettings.maximumIntradayRangePercent, -0.1
                                            )}>−</button>
                                        <input type="number" min="0.01" max="100" step="0.1"
                                            aria-label="5/15-minute high-low range percentage"
                                            value={buySellExecutionSettings.maximumIntradayRangePercent}
                                            onChange={(event) => updateBuySellExecutionSetting(
                                                "maximumIntradayRangePercent", event.target.value
                                            )} />
                                        <button type="button" aria-label="Increase 5/15-minute high-low range percentage"
                                            onClick={() => adjustBuySellIntradayRangePercent(
                                                buySellExecutionSettings.maximumIntradayRangePercent, 0.1
                                            )}>+</button>
                                    </div>
                                    <small>%</small>
                                </div>
                            </div>
                        </div>
                        <div className="buy-sell-strategy-field buy-sell-entry-condition-selector buy-sell-entry-condition-selector--option-wide">
                            <span>CE/PE (LTP) Condition</span>
                            <div className="buy-sell-risk-checks buy-sell-entry-condition-box">
                                <div className="buy-sell-entry-condition-toggles">
                                    {["WH", "WL"].map((level) => <label key={`entry-toggle-${level}`}>
                                        <input type="checkbox" checked={Boolean(entryConditionByLevel.get(level)?.enabled)}
                                            onChange={(event) => updateBuySellEntryCondition(level, "enabled", event.target.checked)} />
                                        {level}
                                    </label>)}
                                </div>
                                <div className="buy-sell-entry-group-slippage">
                                    <span>Slippage</span>
                                    <div className="buy-sell-entry-slippage-stepper">
                                        <button type="button" aria-label="Decrease week slippage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["WH", "WL"], weekSlippage, -0.1
                                            )}>−</button>
                                        <input type="number" min="0.01" max="100" step="0.1"
                                            aria-label="Week slippage percentage"
                                            value={weekSlippage}
                                            onChange={(event) => updateBuySellEntryConditionGroupSlippage(
                                                ["WH", "WL"], event.target.value
                                            )} />
                                        <button type="button" aria-label="Increase week slippage"
                                            onClick={() => adjustBuySellEntryConditionGroupSlippage(
                                                ["WH", "WL"], weekSlippage, 0.1
                                            )}>+</button>
                                    </div>
                                    <small>%</small>
                                </div>
                            </div>
                        </div>
                        </div>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--start"><span>Start Time (24H)</span><select value={buySellExecutionSettings.startTime} onChange={(event) => updateBuySellExecutionSetting("startTime", event.target.value)}>{!EXECUTION_ENTRY_TIME_OPTIONS.includes(buySellExecutionSettings.startTime) && <option value={buySellExecutionSettings.startTime}>{buySellExecutionSettings.startTime}</option>}{EXECUTION_ENTRY_TIME_OPTIONS.map((time) => <option value={time} key={`start-${time}`}>{time}</option>)}</select></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--end"><span>Last Entry (24H)</span><select value={buySellExecutionSettings.endTime} onChange={(event) => updateBuySellExecutionSetting("endTime", event.target.value)}>{!EXECUTION_ENTRY_TIME_OPTIONS.includes(buySellExecutionSettings.endTime) && <option value={buySellExecutionSettings.endTime}>{buySellExecutionSettings.endTime}</option>}{EXECUTION_ENTRY_TIME_OPTIONS.map((time) => <option value={time} key={`end-${time}`}>{time}</option>)}</select></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--square-off"><span>Square Off (24H)</span><select value={buySellExecutionSettings.squareOffTime} onChange={(event) => updateBuySellExecutionSetting("squareOffTime", event.target.value)}>{!EXECUTION_ENTRY_TIME_OPTIONS.includes(buySellExecutionSettings.squareOffTime) && <option value={buySellExecutionSettings.squareOffTime}>{buySellExecutionSettings.squareOffTime}</option>}{EXECUTION_ENTRY_TIME_OPTIONS.map((time) => <option value={time} key={`square-off-${time}`}>{time}</option>)}</select></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--stop-loss"><span>Stop Loss / Trade (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={buySellExecutionSettings.stopLoss} onChange={(event) => updateBuySellExecutionSetting("stopLoss", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--target"><span>Target / Trade (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={buySellExecutionSettings.target} onChange={(event) => updateBuySellExecutionSetting("target", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--daily-loss"><span>Max Daily Loss (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={buySellExecutionSettings.maximumDailyLoss} onChange={(event) => updateBuySellExecutionSetting("maximumDailyLoss", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--max-trades"><span>Max Trades</span><input type="number" min="1" step="1" value={buySellExecutionSettings.maximumTradesPerDay} onChange={(event) => updateBuySellExecutionSetting("maximumTradesPerDay", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--max-open"><span>Max Open Positions</span><input type="number" min="1" step="1" value={buySellExecutionSettings.maximumOpenPositions} onChange={(event) => updateBuySellExecutionSetting("maximumOpenPositions", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--top-gl"><span>TOP (G/L)</span><select value={buySellExecutionSettings.topGainerLoserCount} onChange={(event) => updateBuySellExecutionSetting("topGainerLoserCount", event.target.value)}>{[5, 10, 15, 20].map((count) => <option value={count} key={`top-gl-${count}`}>{count}</option>)}</select></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--max-order"><span>Max Order Value (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={buySellExecutionSettings.maximumOrderValue} onChange={(event) => updateBuySellExecutionSetting("maximumOrderValue", event.target.value)} /></label>
                        <label className="buy-sell-strategy-field buy-sell-risk-field--order-slippage"><span>Order Slippage (%)</span><input type="number" min="0" step="0.01" value={buySellExecutionSettings.maximumSlippagePercent} onChange={(event) => updateBuySellExecutionSetting("maximumSlippagePercent", event.target.value)} /></label>
                        <div className="buy-sell-strategy-field buy-sell-risk-field--allowed-signals"><span>Allowed Signals</span><div className="buy-sell-risk-checks"><label><input type="checkbox" checked={buySellExecutionSettings.allowBuy} onChange={(event) => updateBuySellExecutionSetting("allowBuy", event.target.checked)} /> BUY</label><label><input type="checkbox" checked={buySellExecutionSettings.allowSell} onChange={(event) => updateBuySellExecutionSetting("allowSell", event.target.checked)} /> SELL</label></div></div>
                        <div className="buy-sell-strategy-field buy-sell-risk-field--duplicate"><span>Duplicate Protection</span><div className="buy-sell-risk-checks"><label><input type="checkbox" checked={buySellExecutionSettings.oneTradePerSymbolPerDay} onChange={(event) => updateBuySellExecutionSetting("oneTradePerSymbolPerDay", event.target.checked)} /> One trade/symbol/day</label></div></div>
                    </div>
                    </>
                )}

                {buySellSettingsTab === "EXECUTIONS" && renderBuySellExecutions()}
                {buySellSettingsTab === "HISTORY" && renderBuySellExecutionHistory()}

                <div className="buy-sell-strategy-feedback">
                    <span className={buySellExecutionCapabilities.liveExecutionEnabled
                        ? "buy-sell-backend-ready" : "buy-sell-backend-pending"}>
                        {buySellExecutionCapabilities.liveExecutionEnabled
                            ? "EXECUTION BACKEND READY"
                            : "EXECUTION BACKEND UNAVAILABLE"}
                        {executionSocketConnected ? " · WS CONNECTED" : " · REST POLLING"}
                    </span>
                    {buySellStrategySaveError && <span className="error" role="alert">{buySellStrategySaveError}</span>}
                    {!buySellStrategySaveError && buySellStrategyMessage && <span className="success" role="status">{buySellStrategyMessage}</span>}
                    {buySellStrategySaved && !buySellStrategyMessage && <span className="success">Saved</span>}
                </div>
            </section>
        );
    };

    const renderTop20GainerLoser = () => {
        // The backend applies TOP(G/L) and all risk filters. Render its feed as-is.
        const topGainers = Array.isArray(buySellCandidateRows.TOP_20_GAINER_STOCK)
            ? buySellCandidateRows.TOP_20_GAINER_STOCK : [];
        const topLoosers = Array.isArray(buySellCandidateRows.TOP_20_LOOSER_STOCK)
            ? buySellCandidateRows.TOP_20_LOOSER_STOCK : [];
        const topCount = Number(buySellExecutionSettings.topGainerLoserCount) || 20;

        return <div className="sector-strategies-dashboard top-20-gl-dashboard">
            <div className="strategy-table-stack">
                {renderStrategyStockTable(`TOP ${topCount} GAINER STOCK`, topGainers, "positive", false, "TOP_20_GAINER_STOCK")}
                {renderStrategyStockTable(`TOP ${topCount} LOOSER STOCK`, topLoosers, "negative", false, "TOP_20_LOOSER_STOCK")}
            </div>
        </div>;
    };

    const renderSectorStrategies = () => {
        const topGainerSectors = getStrategyRows("TOP_5_GAINER_SECTOR");
        const topLooserSectors = getStrategyRows("TOP_5_LOOSER_SECTOR");
        const oiSpurts = getStrategyRows("TOP_25_OI_SPURTS");
        const eligibleSymbols = (side) => new Set(
            buySellEntryCalculations
                .filter((calculation) => calculation?.eligible === true && calculation?.side === side)
                .map((calculation) => normalizeSymbol(calculation?.symbol))
                .filter(Boolean)
        );
        const buySymbols = eligibleSymbols("BUY");
        const sellSymbols = eligibleSymbols("SELL");
        const buyRows = getStrategyRows("TOP_20_GAINER_STOCK")
            .filter((row) => buySymbols.has(normalizeSymbol(row?.symbol)));
        const sellRows = getStrategyRows("TOP_20_LOOSER_STOCK")
            .filter((row) => sellSymbols.has(normalizeSymbol(row?.symbol)));

        return <div className="sector-strategies-dashboard sector-strategies-page-dashboard">
            {renderStrategyStockTable(
                "BUY", buyRows, "buy", false, "TOP_20_GAINER_STOCK"
            )}
            {renderStrategyStockTable(
                "SELL", sellRows, "sell", false, "TOP_20_LOOSER_STOCK"
            )}
            <div className="strategy-table-stack">
                {renderStrategySectorTable("TOP 5 GAINER SECTOR", topGainerSectors, "positive")}
                {renderStrategySectorTable("TOP 5 LOOSER SECTOR", topLooserSectors, "negative")}
            </div>
            {renderOiSpurtsTable(oiSpurts)}
        </div>;
    };

    const renderVahValTable = (title, rows, tone, levelColumns, emptyMessage) => {
        const isLoserTable = tone === "negative";
        const listType = isLoserTable ? "TOP_20_LOOSER_STOCK" : "TOP_20_GAINER_STOCK";

        return (
            <section className={`strategy-table-card strategy-table-card--${tone}`}>
                <div className="strategy-table-title">{title} ({rows.length})</div>
                <div className="strategy-table-scroll">
                    <table className="responsive-table table-bordered text-center strategy-table">
                        <thead>
                            <tr>
                                <th>SYMBOL</th>
                                <th>RANK</th>
                                <th>5ML</th>
                                <th>5MH</th>
                                <th>{isLoserTable ? "5ML %" : "5MH %"}</th>
                                <th>15MH %</th>
                                <th>15ML</th>
                                <th>15MH</th>
                                <th>{isLoserTable ? "5ML EP" : "5MH EP"}</th>
                                <th>{isLoserTable ? "15ML EP" : "15MH EP"}</th>
                                <th>LTP</th>
                                {levelColumns.map(([label]) => <th key={label}>{label}</th>)}
                                <th>CHP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length ? rows.map((row, index) => {
                                const entryCalculation = buySellEntryCalculations.find((calculation) =>
                                    normalizeScannerKey(calculation?.listType) === normalizeScannerKey(listType)
                                    && normalizeSymbol(calculation?.symbol) === normalizeSymbol(row?.symbol)
                                );
                                const ltp = toNumber(row?.lp);
                                const fiveMinuteHigh = toNumber(row?.firstFiveMinuteHigh);
                                const fiveMinuteLow = toNumber(row?.firstFiveMinuteLow);
                                const fifteenMinuteHigh = toNumber(row?.firstFifteenMinuteHigh);
                                const fifteenMinuteLow = toNumber(row?.firstFifteenMinuteLow);
                                const fiveMinuteLevelReached = isLoserTable
                                    ? ltp != null && fiveMinuteLow != null && ltp <= fiveMinuteLow
                                    : ltp != null && fiveMinuteHigh != null && ltp >= fiveMinuteHigh;
                                const fifteenMinuteLevelReached = isLoserTable
                                    ? ltp != null && fifteenMinuteLow != null && ltp <= fifteenMinuteLow
                                    : ltp != null && fifteenMinuteHigh != null && ltp >= fifteenMinuteHigh;
                                const ltpRangeClassName = fiveMinuteLevelReached || fifteenMinuteLevelReached
                                    ? isLoserTable ? "strategy-ltp-below-low" : "strategy-ltp-above-high"
                                    : "";
                                const fiveMinuteEntryPrice = isLoserTable
                                    ? entryCalculation?.fiveMinuteLowEntryPrice
                                    : entryCalculation?.fiveMinuteHighEntryPrice;
                                const fifteenMinuteEntryPrice = isLoserTable
                                    ? entryCalculation?.fifteenMinuteLowEntryPrice
                                    : entryCalculation?.fifteenMinuteHighEntryPrice;
                                const fiveMinuteEntryPriceValue = toNumber(fiveMinuteEntryPrice);
                                const fifteenMinuteEntryPriceValue = toNumber(fifteenMinuteEntryPrice);
                                const fiveMinuteEntryPointReached = isLoserTable
                                    ? ltp != null && fiveMinuteEntryPriceValue != null && ltp <= fiveMinuteEntryPriceValue
                                    : ltp != null && fiveMinuteEntryPriceValue != null && ltp >= fiveMinuteEntryPriceValue;
                                const fifteenMinuteEntryPointReached = isLoserTable
                                    ? ltp != null && fifteenMinuteEntryPriceValue != null && ltp <= fifteenMinuteEntryPriceValue
                                    : ltp != null && fifteenMinuteEntryPriceValue != null && ltp >= fifteenMinuteEntryPriceValue;
                                const trendDirection = isLoserTable ? "down" : "up";

                                return (
                                    <tr key={`${row?.symbol ?? title}-${index}`}>
                                        <td className="strategy-symbol">
                                            {row?.symbol?.replace(/^NSE:/, "").replace(/-EQ$/, "") ?? "--"}
                                        </td>
                                        <td>{row?.rank != null ? <strong>#{row.rank}</strong> : "--"}</td>
                                        <td>{formatNumber(row?.firstFiveMinuteLow)}</td>
                                        <td>{formatNumber(row?.firstFiveMinuteHigh)}</td>
                                        <td className={getChangeCellClassName(toNumber(row?.firstFiveMinuteRangePercent))}>
                                            {formatNumber(row?.firstFiveMinuteRangePercent)}%
                                        </td>
                                        <td className={getChangeCellClassName(toNumber(row?.firstFifteenMinuteRangePercent))}>
                                            {formatNumber(row?.firstFifteenMinuteRangePercent)}%
                                        </td>
                                        <td>{formatNumber(row?.firstFifteenMinuteLow)}</td>
                                        <td>{formatNumber(row?.firstFifteenMinuteHigh)}</td>
                                        <td className="strategy-entry-point-cell">
                                            <strong>{formatNumber(fiveMinuteEntryPrice)}</strong>
                                            {fiveMinuteEntryPointReached && <span
                                                className={`strategy-ltp-arrow strategy-ltp-arrow--${trendDirection} strategy-ltp-arrow--right`}
                                                title={`LTP reached the ${isLoserTable ? "5-minute low" : "5-minute high"} entry point`}
                                                aria-label={`LTP reached the ${isLoserTable ? "5-minute low" : "5-minute high"} entry point`}>
                                                {isLoserTable ? "↓" : "↑"}
                                            </span>}
                                        </td>
                                        <td className="strategy-entry-point-cell">
                                            <strong>{formatNumber(fifteenMinuteEntryPrice)}</strong>
                                            {fifteenMinuteEntryPointReached && <span
                                                className={`strategy-ltp-arrow strategy-ltp-arrow--${trendDirection} strategy-ltp-arrow--right`}
                                                title={`LTP reached the ${isLoserTable ? "15-minute low" : "15-minute high"} entry point`}
                                                aria-label={`LTP reached the ${isLoserTable ? "15-minute low" : "15-minute high"} entry point`}>
                                                {isLoserTable ? "↓" : "↑"}
                                            </span>}
                                        </td>
                                        <td className={ltpRangeClassName}>
                                            {fiveMinuteLevelReached && <span
                                                className={`strategy-ltp-arrow strategy-ltp-arrow--${trendDirection} strategy-ltp-arrow--left`}
                                                title={`LTP is at or ${isLoserTable ? "below" : "above"} the 5-minute ${isLoserTable ? "low" : "high"}`}
                                                aria-label={`LTP is at or ${isLoserTable ? "below" : "above"} the 5-minute ${isLoserTable ? "low" : "high"}`}>
                                                {isLoserTable ? "↓" : "↑"}
                                            </span>}
                                            {formatNumber(row?.lp)}
                                            {fifteenMinuteLevelReached && <span
                                                className={`strategy-ltp-arrow strategy-ltp-arrow--${trendDirection} strategy-ltp-arrow--right`}
                                                title={`LTP is at or ${isLoserTable ? "below" : "above"} the 15-minute ${isLoserTable ? "low" : "high"}`}
                                                aria-label={`LTP is at or ${isLoserTable ? "below" : "above"} the 15-minute ${isLoserTable ? "low" : "high"}`}>
                                                {isLoserTable ? "↓" : "↑"}
                                            </span>}
                                        </td>
                                        {levelColumns.map(([label, field]) => (
                                            <td key={`${row?.symbol ?? index}-${label}`}>{formatNumber(row?.[field])}</td>
                                        ))}
                                        <td className={getChangeCellClassName(toNumber(row?.chp))}>
                                            {formatSignedPercent(row?.chp)}
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={levelColumns.length + 12} className="stock-scanner-empty-cell">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    };

    const renderVahVal = () => {
        const topCount = Number(buySellExecutionSettings.topGainerLoserCount) || 20;

        return (
            <div className="sector-strategies-dashboard vah-val-dashboard">
                <div className="strategy-table-stack">
                    {renderVahValTable(
                        `VAH BREAKOUT — TOP ${topCount} GAINERS`,
                        vahBreakoutRows,
                        "positive",
                        [["PDH", "previousDayHigh"], ["VAH", "fixedRangeProfileVah"]],
                        "No top-gainer stocks currently meet the PDH and VAH breakout conditions."
                    )}
                    {renderVahValTable(
                        `VAL BREAKDOWN — TOP ${topCount} LOSERS`,
                        valBreakdownRows,
                        "negative",
                        [["PDL", "previousDayLow"], ["VAL", "fixedRangeProfileVal"]],
                        "No top-loser stocks currently meet the PDL and VAL breakdown conditions."
                    )}
                </div>
            </div>
        );
    };

    const renderAllStockData = () => {
        const rows = getStrategyRows("ALL_STOCK_DATA");

        return (
            <div className="table-scroll-area all-stock-data-scroll">
                <table className="responsive-table table-bordered text-center sticky-header scanner-table pod-table all-stock-data-table">
                    <thead>
                        <tr>
                            <th>SYMBOL</th>
                            <th>LTP</th>
                            <th>CHP</th>
                            <th>PDH</th>
                            <th>PDL</th>
                            <th>PDC</th>
                            <th>VAH</th>
                            <th>VAL</th>
                            <th>POC</th>
                            <th>DH</th>
                            <th>DL</th>
                            <th>OPEN</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((row, index) => {
                            const ltpValue = toNumber(row?.lp);
                            const renderLevelCell = (value, label, isProfileLevel = false, arrowMode = "both") => {
                                const levelValue = toNumber(value);
                                const isAbove = ltpValue != null && levelValue != null && ltpValue > levelValue;
                                const isBelow = ltpValue != null && levelValue != null && ltpValue < levelValue;
                                const arrowDirection = isAbove ? "up" : isBelow ? "down" : null;
                                const showArrow = arrowDirection != null
                                    && (arrowMode === "both" || arrowMode === arrowDirection);

                                return (
                                    <td className={isProfileLevel ? "all-stock-data-profile-level" : undefined}>
                                        {formatNumber(value)}
                                        {showArrow && <span
                                            className={`all-stock-data-level-arrow all-stock-data-level-arrow--${arrowDirection}`}
                                            title={`LTP is ${arrowDirection} ${label}`}
                                            aria-label={`LTP is ${arrowDirection} ${label}`}
                                            role="img"
                                        >{arrowDirection === "up" ? "↑" : "↓"}</span>}
                                    </td>
                                );
                            };

                            return (
                                <tr key={`${row?.symbol ?? "stock"}-${index}`}>
                                    <td className="strategy-symbol">
                                        {row?.symbol?.replace(/^NSE:/, "").replace(/-EQ$/, "") ?? "--"}
                                    </td>
                                    <td>{formatNumber(row?.lp)}</td>
                                    <td>{formatSignedPercent(row?.chp)}</td>
                                    {renderLevelCell(row?.previousDayHigh, "PDH", false, "up")}
                                    {renderLevelCell(row?.previousDayLow, "PDL", false, "down")}
                                    <td>{formatNumber(row?.prev_close_price)}</td>
                                    {renderLevelCell(row?.fixedRangeProfileVah, "VAH", true)}
                                    {renderLevelCell(row?.fixedRangeProfileVal, "VAL", true)}
                                    {renderLevelCell(row?.fixedRangeProfilePoc, "POC", true)}
                                    <td>{formatNumber(row?.high_price)}</td>
                                    <td>{formatNumber(row?.low_price)}</td>
                                    <td>{formatNumber(row?.open_price)}</td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan="12" className="stock-scanner-empty-cell">
                                    Waiting for unfiltered stock data from the strategy websocket...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderBuySellCandidates = () => {
        const buyRows = Array.isArray(buySellCandidateRows.BUY)
            ? buySellCandidateRows.BUY : [];
        const sellRows = Array.isArray(buySellCandidateRows.SELL)
            ? buySellCandidateRows.SELL : [];
        return <div className="sector-strategies-dashboard top-20-gl-dashboard buy-sell-candidates-dashboard">
            <section className="buy-sell-execution-handoff">
                <div>
                    <strong>{buyRows.length + sellRows.length} backend BUY/SELL stocks</strong>
                    <span>Top 20 stocks currently eligible under the enabled entry level, entry band, and selected 5MHL/15MHL range limit. Retest and execution risk limits still apply.</span>
                </div>
                <button type="button" onClick={() => setActiveTab(EXECUTION_RISK_TAB)}>
                    OPEN EXECUTION &amp; RISK
                </button>
            </section>
            {renderStrategyStockTable("BUY", buyRows, "buy", false, "TOP_20_GAINER_STOCK")}
            {renderStrategyStockTable("SELL", sellRows, "sell", false, "TOP_20_LOOSER_STOCK")}
        </div>;
    };

    const renderExecutionRiskDashboard = () => {
        const enabledConditions = (buySellExecutionSettings.entryConditions ?? [])
            .filter((condition) => condition.enabled);
        const buyCandidates = buySellEntryCalculations.filter(
            (calculation) => calculation?.eligible === true && calculation?.side === "BUY"
        );
        const sellCandidates = buySellEntryCalculations.filter(
            (calculation) => calculation?.eligible === true && calculation?.side === "SELL"
        );
        const currentCandidateCount = buyCandidates.length + sellCandidates.length;
        const runningTrades = buySellTrades.filter((trade) => [
            "ENTRY_SUBMITTED", "OPEN", "EXIT_SUBMITTED",
        ].includes(trade?.status));
        const openTrades = runningTrades.filter((trade) => trade?.status === "OPEN");
        const closedTrades = buySellTrades.filter((trade) => trade?.status === "CLOSED");
        const totalPositionCount = openTrades.length + closedTrades.length;
        const runningPnl = openTrades.reduce(
            (total, trade) => total + (toNumber(trade?.unrealizedPnl) ?? 0),
            0
        );
        const bookedPnl = closedTrades.reduce(
            (total, trade) => total + (toNumber(trade?.realizedPnl) ?? 0),
            0
        );
        const totalPnl = runningPnl + bookedPnl;
        const selectedBroker = buySellBrokers.find(
            (broker) => String(getBrokerId(broker)) === selectedBuySellBrokerId
        );
        const brokerLabel = selectedBroker
            ? selectedBroker?.accountName ?? selectedBroker?.brokerType ?? "Selected broker"
            : "Paper only";
        const activatedTradingMode = buySellStrategyStatus === "ACTIVE"
            ? buySellActivatedTradingMode
            : "";

        return <div className="execution-risk-dashboard">
            <section className="execution-risk-page-header">
                <div>
                    <span className="execution-risk-eyebrow">AUTOMATIC TRADE CONTROL</span>
                    <h2>Execution &amp; Risk</h2>
                    <p>
                        Configure execution, start paper or live trading, monitor orders and
                        square off positions from one workspace.
                    </p>
                </div>
                <span className={`buy-sell-strategy-status buy-sell-strategy-status--${buySellStrategyStatus.toLowerCase()}`}>
                    {buySellStrategyStatus === "ACTIVE" && activatedTradingMode
                        ? `${buySellStrategyStatus} · ${activatedTradingMode}`
                        : buySellStrategyStatus}
                </span>
            </section>

            <section className="execution-risk-summary-grid" aria-label="Execution summary">
                <div className="execution-risk-summary-card">
                    <span>CURRENT CANDIDATES</span>
                    <strong>{currentCandidateCount}</strong>
                    <small>{buyCandidates.length} BUY · {sellCandidates.length} SELL matching enabled entry bands</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>TRADING MODE</span>
                    <strong>{activatedTradingMode ?? "NOT ACTIVE"}</strong>
                    <small>{activatedTradingMode
                        ? `${buySellExecutionSettings.productType} · ${buySellExecutionSettings.orderType}`
                        : "Use an activation button to start"}</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>POSITIONS ({totalPositionCount})</span>
                    <strong>{openTrades.length} OPEN</strong>
                    <small>{closedTrades.length} CLOSED</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>RUNNING P&amp;L</span>
                    <strong className={getChangeCellClassName(runningPnl)}>
                        ₹{formatNumber(runningPnl)}
                    </strong>
                    <small>Open positions</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>BOOKED P&amp;L</span>
                    <strong className={getChangeCellClassName(bookedPnl)}>
                        ₹{formatNumber(bookedPnl)}
                    </strong>
                    <small>Closed positions</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>TOTAL P&amp;L</span>
                    <strong className={getChangeCellClassName(totalPnl)}>
                        ₹{formatNumber(totalPnl)}
                    </strong>
                    <small>Running plus booked</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>BROKER</span>
                    <strong>{brokerLabel}</strong>
                    <small>{selectedBroker
                        ? isBrokerReady(selectedBroker) ? "CONNECTED" : "DISCONNECTED"
                        : "Not required for paper"}</small>
                </div>
                <div className="execution-risk-summary-card">
                    <span>EXECUTION FEED</span>
                    <strong>{executionSocketConnected ? "CONNECTED" : "POLLING"}</strong>
                    <small>{buySellExecutionCapabilities.liveExecutionEnabled
                        ? "Backend execution available" : "Backend execution unavailable"}</small>
                </div>
            </section>

            {renderBuySellStrategySetup()}

            {renderRunningPositions(runningTrades)}
        </div>;
    };

    const renderPreOpenBook = (row, columnCount) => {
        const preOpenMarket = row.preOpenMarket ?? {};
        const orderRows = Array.isArray(preOpenMarket.preopen)
            ? preOpenMarket.preopen
            : Array.isArray(preOpenMarket.preOpen)
                ? preOpenMarket.preOpen
                : [];
        const ato = preOpenMarket.ato ?? {};
        const totalBuyQuantity =
            preOpenMarket.totalBuyQuantity ?? preOpenMarket.totalBuyQty;
        const totalSellQuantity =
            preOpenMarket.totalSellQuantity ?? preOpenMarket.totalSellQty;
        const equilibriumPrice =
            preOpenMarket.IEP ?? preOpenMarket.iep ?? row.iep;
        const equilibriumQuantity =
            preOpenMarket.indicativeEquilibriumQuantity ??
            preOpenMarket.iepQuantity ??
            preOpenMarket.totalTradedVolume ??
            row.finalQuantity;
        const changeValue = toNumber(row.change);
        const pChangeValue = toNumber(row.pChange);
        const summaryRows = [
            ["Indicative Equilibrium Price (In ₹)", formatPreOpenPrice(equilibriumPrice)],
            ["Indicative Equilibrium Quantity", formatPreOpenQuantity(equilibriumQuantity)],
            ["Final Price (In ₹)", formatPreOpenPrice(row.finalPrice)],
            ["Final Quantity", formatPreOpenQuantity(row.finalQuantity)],
            ["Previous Close", formatPreOpenPrice(row.previousClose)],
            ["Change", formatPreOpenPrice(changeValue), getChangeCellClassName(changeValue)],
            ["%Change", pChangeValue == null ? "-" : formatPreOpenNumber(pChangeValue),
                getChangeCellClassName(pChangeValue)],
        ];

        return (
            <tr className="pod-pre-open-details-row">
                <td colSpan={columnCount} className="pod-pre-open-details-cell">
                    <div className="pod-pre-open-book">
                        <div className="pod-pre-open-book-title">
                            {row.symbol}
                        </div>
                        <div className="pod-pre-open-book-grid">
                            <table className="pod-book-table">
                                <thead>
                                    <tr>
                                        <th>BUY QTY</th>
                                        <th>PRICE</th>
                                        <th>SELL QTY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderRows.length > 0 ? orderRows.map((order, index) => {
                                        const orderPrice =
                                            order?.price ?? order?.orderPrice ?? order?.value;
                                        const isEquilibriumPrice = Boolean(order?.iep) ||
                                            (toNumber(orderPrice) != null &&
                                                toNumber(orderPrice) === toNumber(equilibriumPrice));

                                        return (
                                            <tr key={`${row.key}-book-${index}`}>
                                                <td>{formatPreOpenQuantity(
                                                    order?.buyQty ?? order?.buyQuantity ?? order?.buy
                                                )}</td>
                                                <td className={isEquilibriumPrice ? "pod-book-iep-price" : "pod-book-price"}>
                                                    {formatPreOpenPrice(orderPrice)}
                                                </td>
                                                <td>{formatPreOpenQuantity(
                                                    order?.sellQty ?? order?.sellQuantity ?? order?.sell
                                                )}</td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan="3" className="pod-book-empty-cell">
                                                No pre-open order-book data available.
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="pod-book-footer-heading">
                                        <th>BUY QTY</th>
                                        <th aria-label="Order type" />
                                        <th>SELL QTY</th>
                                    </tr>
                                    <tr>
                                        <td>{formatPreOpenQuantity(
                                            ato?.buy ?? ato?.buyQty ?? preOpenMarket.atoBuyQty
                                        )}</td>
                                        <td className="pod-book-price">At the Open (ATO)</td>
                                        <td>{formatPreOpenQuantity(
                                            ato?.sell ?? ato?.sellQty ?? preOpenMarket.atoSellQty
                                        )}</td>
                                    </tr>
                                    <tr>
                                        <td>{formatPreOpenQuantity(totalBuyQuantity)}</td>
                                        <td className="pod-book-price">Total</td>
                                        <td>{formatPreOpenQuantity(totalSellQuantity)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table className="pod-summary-table">
                                <thead>
                                    <tr>
                                        <th colSpan="2">ORDER COLLECTION SUMMARY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryRows.map(([label, value, valueClassName = ""]) => (
                                        <tr key={label}>
                                            <td>{label}</td>
                                            <td className={valueClassName}>{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    const renderPreOpenTable = (
        rows = stockPreOpenRows,
        useContentHeight = false,
        emptyMessage = "Waiting for stock pre-open data...",
        showOpenPrice = false,
        showChart = true
    ) => {
        const columnCount = 17 + Number(showOpenPrice) + Number(showChart);
        const sectorFilteredRows = isStrongSignalSectorFilterEnabled
            ? rows.filter((row) => topBottomSectorSymbolSet.has(normalizeSymbol(row.symbol)))
            : rows;
        const strongSignalFilteredRows = strongSignalFilters.length === 0
            ? sectorFilteredRows
            : sectorFilteredRows.filter((row) => strongSignalFilters.includes(getStrongSignal(row)));
        const openRangeFilteredRows = isOgsrFilterEnabled || isOlsrFilterEnabled
            ? strongSignalFilteredRows.filter((row) => {
                const finalPrice = toNumber(row.finalPrice);
                const support = toNumber(row.support);
                const resistance = toNumber(row.resistance);

                if (finalPrice == null || support == null || resistance == null) {
                    return false;
                }

                const matchesOgsr = isOgsrFilterEnabled &&
                    finalPrice >= support && finalPrice >= resistance;
                const matchesOlsr = isOlsrFilterEnabled &&
                    finalPrice <= support && finalPrice <= resistance;

                return matchesOgsr || matchesOlsr;
            })
            : strongSignalFilteredRows;
        const ltpRangeFilteredRows = isLtpgsrFilterEnabled || isLtplsrFilterEnabled
            ? openRangeFilteredRows.filter((row) => {
                const ltp = toNumber(row.ltp);
                const support = toNumber(row.support);
                const resistance = toNumber(row.resistance);

                if (ltp == null || support == null || resistance == null) {
                    return false;
                }

                const matchesLtpgsr = isLtpgsrFilterEnabled &&
                    ltp >= support && ltp >= resistance;
                const matchesLtplsr = isLtplsrFilterEnabled &&
                    ltp <= support && ltp <= resistance;

                return matchesLtpgsr || matchesLtplsr;
            })
            : openRangeFilteredRows;
        const visibleRows = isLtpg500FilterEnabled
            ? ltpRangeFilteredRows.filter((row) => {
                const ltp = toNumber(row.ltp);

                return ltp != null && ltp >= 500;
            })
            : ltpRangeFilteredRows;
        const activePodSortDirection = livePercentSortDirection ??
            oiPercentSortDirection ?? resistanceSupportSortDirection;
        const getActivePodSortValue = livePercentSortDirection
            ? (row) => row.chp
            : oiPercentSortDirection
                ? (row) => row.avgInOI
                : resistanceSupportSortDirection
                    ? (row) => {
                        const resistance = toNumber(row.resistance);
                        const support = toNumber(row.support);

                        return resistance == null || support == null
                            ? null
                            : resistance - support;
                    }
                    : null;
        const sortedVisibleRows = activePodSortDirection == null || getActivePodSortValue == null
            ? visibleRows
            : visibleRows
                .map((row, index) => ({ row, index }))
                .sort((firstRow, secondRow) => {
                    const firstSortValue = toNumber(getActivePodSortValue(firstRow.row));
                    const secondSortValue = toNumber(getActivePodSortValue(secondRow.row));

                    if (firstSortValue == null && secondSortValue == null) {
                        return firstRow.index - secondRow.index;
                    }

                    if (firstSortValue == null) return 1;
                    if (secondSortValue == null) return -1;

                    const sortDifference = firstSortValue - secondSortValue;

                    if (sortDifference === 0) {
                        return firstRow.index - secondRow.index;
                    }

                    return activePodSortDirection === "asc"
                        ? sortDifference
                        : -sortDifference;
                })
                .map(({ row }) => row);
        const activeEmptyMessage = rows.length > 0 && visibleRows.length === 0
            ? "No POD data matching selected STRNG SIGNAL/Sector filter."
            : emptyMessage;
        const selectedPodFilters = [
            ...strongSignalFilters,
            ...(isStrongSignalSectorFilterEnabled ? ["SECTOR"] : []),
            ...(isLtpgsrFilterEnabled ? ["LTPGSR"] : []),
            ...(isLtplsrFilterEnabled ? ["LTPLSR"] : []),
            ...(isOgsrFilterEnabled ? ["OGSR"] : []),
            ...(isOlsrFilterEnabled ? ["OLSR"] : []),
            ...(isLtpg500FilterEnabled ? ["LTPG500"] : []),
        ];
        const strongSignalActiveFilterCount = selectedPodFilters.length;
        const strongSignalFilterLabel = strongSignalActiveFilterCount === 0
            ? "ALL"
            : strongSignalActiveFilterCount === 1
                ? selectedPodFilters[0]
                : `${strongSignalActiveFilterCount} SELECTED`;
        const oiPercentSortIcon = oiPercentSortDirection === "asc"
            ? "▲"
            : oiPercentSortDirection === "desc"
                ? "▼"
                : "↕";
        const livePercentSortIcon = livePercentSortDirection === "asc"
            ? "▲"
            : livePercentSortDirection === "desc"
                ? "▼"
                : "↕";
        const resistanceSupportSortIcon = resistanceSupportSortDirection === "asc"
            ? "▲"
            : resistanceSupportSortDirection === "desc"
                ? "▼"
                : "↕";

        return (
        <div className={`table-scroll-area ${useContentHeight ? "mc-filter-table-scroll-area" : ""}`}>
            <table className="responsive-table table-bordered text-center sticky-header scanner-table pod-table">
                <thead>
                    <tr>
                        <th>SYMBOL</th>
                        {showChart && <th>CHART</th>}
                        {/* <th>BUY QTY</th>
                        <th>SELL QTY</th> */}
                        <th>SIGNAL</th>
                        <th>PDC</th>
                        <th>SUP</th>
                        <th>RES</th>
                        <th
                            className="pod-oi-sort-header"
                            aria-sort={
                                resistanceSupportSortDirection === "asc"
                                    ? "ascending"
                                    : resistanceSupportSortDirection === "desc"
                                        ? "descending"
                                        : "none"
                            }
                        >
                            <button
                                type="button"
                                className={`pod-oi-sort-button ${resistanceSupportSortDirection ? "active" : ""}`}
                                onClick={toggleResistanceSupportSort}
                                title="Click: ASC, DESC, NORMAL"
                            >
                                <span>R-S</span>
                                <span className="pod-oi-sort-icon">{resistanceSupportSortIcon}</span>
                            </button>
                        </th>
                        <th>OPEN</th>
                        <th>LTP</th>
                        <th>1st 5MH</th>
                        <th>1st 5ML</th>
                        <th>BSIGNAL</th>
                        <th className="pod-strong-signal-header">
                            <div className="pod-strong-signal-header-content">
                                <span>STRNG SIGNAL</span>
                                <div
                                    className="pod-strong-signal-filter"
                                    ref={strongSignalFilterRef}
                                >
                                    <button
                                        type="button"
                                        className={`pod-filter-button ${strongSignalActiveFilterCount > 0 ? "active" : ""}`}
                                        aria-haspopup="listbox"
                                        aria-expanded={isStrongSignalFilterOpen}
                                        onClick={() => setIsStrongSignalFilterOpen((isOpen) => !isOpen)}
                                    >
                                        {strongSignalFilterLabel}
                                    </button>
                                    {isStrongSignalFilterOpen && (
                                        <div
                                            className="pod-filter-menu"
                                            role="listbox"
                                            aria-label="Filter strong signal"
                                        >
                                            {STRONG_SIGNAL_OPTIONS.map((signal) => (
                                                <label
                                                    className="pod-filter-option"
                                                    key={signal}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={strongSignalFilters.includes(signal)}
                                                        onChange={() => toggleStrongSignalFilter(signal)}
                                                    />
                                                    <span>{signal}</span>
                                                </label>
                                            ))}
                                            <label className="pod-filter-option pod-filter-option-sector">
                                                <input
                                                    type="checkbox"
                                                    checked={isStrongSignalSectorFilterEnabled}
                                                    onChange={() => setIsStrongSignalSectorFilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>SECTOR</span>
                                            </label>
                                            <label className="pod-filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={isLtpgsrFilterEnabled}
                                                    onChange={() => setIsLtpgsrFilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>LTPGSR</span>
                                            </label>
                                            <label className="pod-filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={isLtplsrFilterEnabled}
                                                    onChange={() => setIsLtplsrFilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>LTPLSR</span>
                                            </label>
                                            <label className="pod-filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={isOgsrFilterEnabled}
                                                    onChange={() => setIsOgsrFilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>OGSR</span>
                                            </label>
                                            <label className="pod-filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={isOlsrFilterEnabled}
                                                    onChange={() => setIsOlsrFilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>OLSR</span>
                                            </label>
                                            <label className="pod-filter-option">
                                                <input
                                                    type="checkbox"
                                                    checked={isLtpg500FilterEnabled}
                                                    onChange={() => setIsLtpg500FilterEnabled(
                                                        (isEnabled) => !isEnabled
                                                    )}
                                                />
                                                <span>LTPG500</span>
                                            </label>
                                            <button
                                                type="button"
                                                className="pod-filter-clear-button"
                                                onClick={() => {
                                                    setStrongSignalFilters([]);
                                                    setIsStrongSignalSectorFilterEnabled(false);
                                                    setIsOgsrFilterEnabled(false);
                                                    setIsOlsrFilterEnabled(false);
                                                    setIsLtpg500FilterEnabled(false);
                                                    setIsLtpgsrFilterEnabled(false);
                                                    setIsLtplsrFilterEnabled(false);
                                                }}
                                            >
                                                CLEAR
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </th>
                        <th>SECTOR</th>
                        <th>SECTOR%</th>
                        <th
                            className="pod-oi-sort-header"
                            aria-sort={
                                oiPercentSortDirection === "asc"
                                    ? "ascending"
                                    : oiPercentSortDirection === "desc"
                                        ? "descending"
                                        : "none"
                            }
                        >
                            <button
                                type="button"
                                className={`pod-oi-sort-button ${oiPercentSortDirection ? "active" : ""}`}
                                onClick={toggleOiPercentSort}
                                title="Click: ASC, DESC, NORMAL"
                            >
                                <span>OI%</span>
                                <span className="pod-oi-sort-icon">{oiPercentSortIcon}</span>
                            </button>
                        </th>
                        {showOpenPrice && <th>OPEN PRICE</th>}
                        <th
                            className="pod-live-percent-sort-header"
                            aria-sort={
                                livePercentSortDirection === "asc"
                                    ? "ascending"
                                    : livePercentSortDirection === "desc"
                                        ? "descending"
                                        : "none"
                            }
                        >
                            <button
                                type="button"
                                className={`pod-live-percent-sort-button ${livePercentSortDirection ? "active" : ""}`}
                                onClick={toggleLivePercentSort}
                                title="Click: ASC, DESC, NORMAL"
                            >
                                <span>LIVEP%</span>
                                <span className="pod-live-percent-sort-icon">{livePercentSortIcon}</span>
                            </button>
                        </th>
                        <th>%CHNG</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedVisibleRows.length > 0 ? (
                        sortedVisibleRows.map((row) => {
                            const livePercentValue = toNumber(row.chp);
                            const livePercentText = livePercentValue == null
                                ? "--"
                                : `${livePercentValue > 0 ? "+" : ""}${formatPreOpenNumber(livePercentValue)}%`;
                            const pChangeValue = toNumber(row.pChange);
                            const sectorPercentValue = toNumber(row.sectorPercent);
                            const sectorPercentText = sectorPercentValue == null
                                ? "--"
                                : `${sectorPercentValue > 0 ? "+" : ""}${formatPreOpenNumber(sectorPercentValue)}%`;
                            const isExpanded = expandedPreOpenRowKey === row.key;
                            const ltpValue = toNumber(row.ltp);
                            const finalPriceValue = toNumber(row.finalPrice);
                            const previousCloseValue = toNumber(row.previousClose);
                            const supportValue = toNumber(row.support);
                            const resistanceValue = toNumber(row.resistance);
                            const resistanceSupportValue =
                                resistanceValue == null || supportValue == null
                                    ? null
                                    : resistanceValue - supportValue;
                            const ltpComparisonClass =
                                ltpValue == null ||
                                finalPriceValue == null ||
                                previousCloseValue == null
                                ? ""
                                : ltpValue > finalPriceValue && ltpValue > previousCloseValue
                                    ? "pod-ltp-above-final"
                                    : ltpValue < finalPriceValue && ltpValue < previousCloseValue
                                        ? "pod-ltp-below-final"
                                        : "";
                            const podSignal = getPodSignal(row);
                            const podSignalClassName = getPodSignalClassName(podSignal);
                            const breakoutSignal = getBreakoutSignal(row);
                            const breakoutSignalClassName = getPodSignalClassName(breakoutSignal);
                            const strongSignal = getStrongSignal(row);
                            const strongSignalClassName = getPodSignalClassName(strongSignal);

                            return (
                                <React.Fragment key={row.key}>
                                <tr>
                                    <td>
                                        <button
                                            type="button"
                                            className="pod-symbol-button"
                                            aria-expanded={isExpanded}
                                            onClick={() => setExpandedPreOpenRowKey(
                                                isExpanded ? null : row.key
                                            )}
                                        >
                                            {row.symbol}
                                        </button>
                                    </td>
                                    {showChart && (
                                        <td>
                                            <button
                                                type="button"
                                                className="pod-chart-button"
                                                aria-label={`Open ${row.symbol} pre-open chart`}
                                                title={`Open ${row.symbol} chart`}
                                                onClick={() => openPreOpenChart(row)}
                                            >
                                                <ChartLine aria-hidden="true" />
                                            </button>
                                        </td>
                                    )}
                                    {/* <td>{formatPreOpenQuantity(buyQuantity)}</td>
                                    <td>{formatPreOpenQuantity(sellQuantity)}</td> */}
                                    <td>
                                        <span className={`pod-signal ${podSignalClassName}`}>
                                            {podSignal}
                                        </span>
                                    </td>
                                    <td>{formatPreOpenNumber(row.previousClose)}</td>
                                    <td>{formatPreOpenNumber(row.support)}</td>
                                    <td>{formatPreOpenNumber(row.resistance)}</td>
                                    <td>{formatPreOpenNumber(resistanceSupportValue)}</td>
                                    <td>{formatPreOpenNumber(row.finalPrice)}</td>
                                    <td className={ltpComparisonClass}>
                                        {formatPreOpenNumber(row.ltp)}
                                    </td>
                                    <td>{formatPreOpenNumber(row.firstFiveMinuteHigh)}</td>
                                    <td>{formatPreOpenNumber(row.firstFiveMinuteLow)}</td>
                                    <td>
                                        <span className={`pod-signal ${breakoutSignalClassName}`}>
                                            {breakoutSignal}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`pod-signal pod-strong-signal ${strongSignalClassName}`}>
                                            {strongSignal}
                                        </span>
                                    </td>
                                    <td>{formatSectorDisplayName(row.sectorName)}</td>
                                    <td className={getChangeCellClassName(sectorPercentValue)}>
                                        {sectorPercentText}
                                    </td>
                                    <td>{formatPreOpenNumber(row.avgInOI)}</td>
                                    {showOpenPrice && (
                                        <td>{formatPreOpenNumber(row.openPrice)}</td>
                                    )}
                                    <td className={`pod-live-percent-value ${getChangeCellClassName(livePercentValue)}`}>
                                        {livePercentText}
                                    </td>
                                    <td className={getChangeCellClassName(pChangeValue)}>
                                        {pChangeValue > 0 ? "+" : ""}{formatPreOpenNumber(pChangeValue)}%
                                    </td>
                                </tr>
                                {isExpanded && renderPreOpenBook(row, columnCount)}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={columnCount}
                                className="stock-scanner-empty-cell"
                            >
                                {activeEmptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
        );
    };

    const preOpenChartPriceRange = getPreOpenChartPriceRange(
        preOpenChart.data?.grapthData
    );
    const buySellBuyCount = Array.isArray(buySellCandidateRows.BUY)
        ? buySellCandidateRows.BUY.length
        : 0;
    const buySellSellCount = Array.isArray(buySellCandidateRows.SELL)
        ? buySellCandidateRows.SELL.length
        : 0;

    return (
        <div className="stock-scanner" ref={stockScannerRef}>
            <div className="sticky-top-shell" ref={stickyTopShellRef}>
                <header className="header">
                    <nav className="nav-container">
                        <div className="header-flex">
                            <div className="left-nav">
                                <div className="logo-container">
                                    <img
                                        src="/assets/images/noptionwave-logo-1.png"
                                        className="header-logo"
                                        alt="logo"
                                    />
                                </div>

                                {topBottomHeaderData.map((item, idx) => {
                                    const color = getChpColor(item.value2);

                                    return (
                                        <div className="header-item" key={`${item.label1}-${idx}`}>
                                            <span className="header-line" style={{ color }}>
                                                {item.label1} : {item.value1}
                                            </span>
                                            <span className="header-line" style={{ color }}>
                                                {item.label2} : {item.value2}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    Date : {headerDate}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    Time : {headerTime}
                                </span>
                            </div>
                        </div>
                    </nav>
                </header>

                <div className="tab-container">
                    <div className="tab">
                        <button
                            className={`tablinks sub-header-font ${activeTab === BUY_SELL_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(BUY_SELL_TAB)}
                        >
                            BUY({buySellBuyCount})/SELL({buySellSellCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === EXECUTION_RISK_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(EXECUTION_RISK_TAB)}
                        >
                            EXECUTION &amp; RISK
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === TOP_20_GL_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(TOP_20_GL_TAB)}
                        >
                            TOP ({[5, 10, 15, 20].includes(Number(buySellExecutionSettings.topGainerLoserCount)) ? buySellExecutionSettings.topGainerLoserCount : 20}) G/L
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === POD_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(POD_TAB)}
                        >
                            POD ({stockPreOpenCount})
                        </button>
                    </div>
                </div>

                {activeTab === POD_TAB ? (
                    <div className="fixed-active-scanner-title pod-adu-summary-row">
                        <div className="pod-adu-card pod-adu-card-advances">
                            <TrendingUp aria-hidden="true" className="pod-adu-icon" />
                            <span className="pod-adu-label">Advances</span>
                            <span className="pod-adu-value">{podAdvancesCount}</span>
                        </div>
                        <div className="pod-adu-card pod-adu-card-declines">
                            <TrendingDown aria-hidden="true" className="pod-adu-icon" />
                            <span className="pod-adu-label">Declines</span>
                            <span className="pod-adu-value">{podDeclinesCount}</span>
                        </div>
                        <div className="pod-adu-card pod-adu-card-unchanged">
                            <Minus aria-hidden="true" className="pod-adu-icon" />
                            <span className="pod-adu-label">Unchanged</span>
                            <span className="pod-adu-value">{podUnchangedCount}</span>
                        </div>
                        <div className="pod-adu-card pod-adu-card-filtered">
                            <span className="pod-adu-label">
                                {podSelectedFilterCount > 0 ? "Filtered Stocks" : "Total Stocks"}
                            </span>
                            <span className="pod-adu-value">{podFilteredStockCount}</span>
                        </div>
                    </div>
                ) : activeFixedTitle && (
                    <div className="fixed-active-scanner-title">
                        {activeFixedTitle.label} ({activeFixedTitle.count})
                    </div>
                )}
            </div>

            <div className="table-container">
                {activeTab === EXECUTION_RISK_TAB && renderExecutionRiskDashboard()}
                {activeTab === TOP_20_GL_TAB && renderTop20GainerLoser()}
                {activeTab === BUY_SELL_TAB && renderBuySellCandidates()}
                {activeTab === POD_TAB && renderPreOpenTable()}
                {activeTab === SECTOR_STRATEGIES_TAB && renderSectorStrategies()}
                {activeTab === VAH_VAL_TAB && renderVahVal()}
                {activeTab === ALL_STOCK_DATA_TAB && renderAllStockData()}
            </div>

            <div className="fixed-footer">
                <div className="footer-left">
                    <span>noptionwave © 2026</span>
                </div>
            </div>

            {entryHistoryPopup && (
                <div className="strategy-history-modal-backdrop" role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setEntryHistoryPopup(null);
                    }}>
                    <section className="strategy-history-modal" role="dialog" aria-modal="true"
                        aria-labelledby="strategy-history-title">
                        <div className="strategy-history-modal-header">
                            <h2 id="strategy-history-title">
                                {entryHistoryPopup.side} ENTRY HISTORY — {entryHistoryPopup.symbol}
                            </h2>
                            <button type="button" className="strategy-history-close-button"
                                aria-label="Close entry history" onClick={() => setEntryHistoryPopup(null)}>
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        <div className="strategy-history-modal-body">
                            {entryHistoryPopup.history.length ? (
                                <ol className="strategy-history-list">
                                    {entryHistoryPopup.history.map((timestamp, index) => (
                                        <li key={`${timestamp}-${index}`}>
                                            <span>#{entryHistoryPopup.history.length - index}</span>
                                            <strong>{timestamp}</strong>
                                        </li>
                                    ))}
                                </ol>
                            ) : <div className="strategy-history-empty">No entry history available.</div>}
                        </div>
                    </section>
                </div>
            )}

            {liveRankHistoryPopup && (
                <div className="strategy-history-modal-backdrop" role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setRankHistoryPopup(null);
                    }}>
                    <section className="strategy-history-modal strategy-rank-history-modal" role="dialog"
                        aria-modal="true" aria-labelledby="strategy-rank-history-title">
                        <div className="strategy-history-modal-header strategy-rank-history-modal-header">
                            <div>
                                <h2 id="strategy-rank-history-title">
                                    RANK HISTORY — {liveRankHistoryPopup.symbol}
                                </h2>
                                <span>{liveRankHistoryPopup.listTitle} · LIVE UPDATES</span>
                            </div>
                            <button type="button" className="strategy-history-close-button"
                                aria-label="Close rank history" onClick={() => setRankHistoryPopup(null)}>
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        <div className="strategy-history-modal-body">
                            <div className="strategy-rank-current-summary">
                                <div className={liveRankHistoryPopup.isCurrentlyRanked
                                    ? "strategy-rank-summary-active" : "strategy-rank-summary-out"}>
                                    <span>Status</span>
                                    <strong>{liveRankHistoryPopup.isCurrentlyRanked ? "IN TOP 20" : "OUT OF TOP 20"}</strong>
                                </div>
                                <div><span>Current rank</span><strong>{liveRankHistoryPopup.isCurrentlyRanked
                                    ? `#${liveRankHistoryPopup.rank}` : "OUT"}</strong></div>
                                <div><span>Live LTP</span><strong>{formatNumber(liveRankHistoryPopup.ltp)}</strong></div>
                                <div><span>Stock %</span><strong>{formatNumber(liveRankHistoryPopup.changePercentage)}%</strong></div>
                                <div><span>Current entry</span><strong>{liveRankHistoryPopup.entryDate || "--"} {liveRankHistoryPopup.entryTime || ""}</strong></div>
                                <div><span>Entries today</span><strong>{liveRankHistoryPopup.entryCount}</strong></div>
                            </div>
                            {liveRankHistoryPopup.history.length ? (
                                <ol className="strategy-rank-history-list">
                                    {liveRankHistoryPopup.history.map((event, index) => {
                                        const eventType = String(event?.eventType ?? "RANK_CHANGED").toUpperCase();
                                        const eventRank = toNumber(event?.rank);
                                        const previousRank = toNumber(event?.previousRank);
                                        const eventLabel = eventType === "RE_ENTERED"
                                            ? "RE-ENTERED"
                                            : eventType === "RANK_CHANGED"
                                                ? "RANK CHANGED"
                                                : eventType;
                                        const movement = eventType === "EXITED"
                                            ? `#${previousRank ?? eventRank ?? "--"} → OUT`
                                            : previousRank != null
                                                ? `#${previousRank} → #${eventRank ?? "--"}`
                                                : `ENTERED AT #${eventRank ?? "--"}`;

                                        return <li key={`${event?.dateTime ?? event?.localTime ?? "event"}-${index}`}>
                                            <div className="strategy-rank-history-event-header">
                                                <span className={`strategy-rank-event strategy-rank-event--${eventType.toLowerCase().replace("_", "-")}`}>
                                                    {eventLabel}
                                                </span>
                                                <time>{event?.dateTime ?? `${event?.localDate ?? "--"} ${event?.localTime ?? ""}`}</time>
                                            </div>
                                            <strong className="strategy-rank-history-movement">{movement}</strong>
                                            <div className="strategy-rank-history-values">
                                                <span>Stock % <strong>{formatNumber(event?.changePercentage)}%</strong></span>
                                                <span>LTP <strong>{formatNumber(event?.ltp)}</strong></span>
                                            </div>
                                        </li>;
                                    })}
                                </ol>
                            ) : <div className="strategy-history-empty">
                                Rank history will appear after the updated backend starts publishing rank events.
                            </div>}
                        </div>
                    </section>
                </div>
            )}

            {preOpenChart.isOpen && (
                <div
                    className="pod-chart-modal-backdrop"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closePreOpenChart();
                    }}
                >
                    <section
                        className="pod-chart-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pod-chart-symbol"
                    >
                        <div className="pod-chart-modal-header">
                            <h2 id="pod-chart-symbol" className="pod-chart-modal-heading">
                                <span className="pod-chart-heading-symbol">
                                    {preOpenChart.data?.identifier || preOpenChart.symbol}
                                </span>
                                {(preOpenChart.resistance ?? preOpenChartPriceRange.highest) != null && (
                                    <span className="pod-chart-heading-high">
                                        RES: {formatPreOpenNumber(
                                            preOpenChart.resistance ?? preOpenChartPriceRange.highest
                                        )}
                                    </span>
                                )}
                                {(preOpenChart.support ?? preOpenChartPriceRange.lowest) != null && (
                                    <span className="pod-chart-heading-low">
                                        SUP: {formatPreOpenNumber(
                                            preOpenChart.support ?? preOpenChartPriceRange.lowest
                                        )}
                                    </span>
                                )}
                            </h2>
                            <button
                                type="button"
                                className="pod-chart-close-button"
                                aria-label="Close chart"
                                onClick={closePreOpenChart}
                            >
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        <div className="pod-chart-modal-body">
                            {preOpenChart.isLoading && (
                                <div className="pod-chart-message">Loading chart...</div>
                            )}
                            {!preOpenChart.isLoading && preOpenChart.error && (
                                <div className="pod-chart-message pod-chart-error">
                                    {preOpenChart.error}
                                </div>
                            )}
                            {!preOpenChart.isLoading && !preOpenChart.error && preOpenChart.data && (() => {
                                const chartPoints = preOpenChart.data.grapthData
                                    .map(([timestamp, price]) => ({
                                        timestamp: Number(timestamp),
                                        price: Number(price),
                                    }))
                                    .filter((point) =>
                                        Number.isFinite(point.timestamp) && Number.isFinite(point.price)
                                    )
                                    .sort((firstPoint, secondPoint) =>
                                        firstPoint.timestamp - secondPoint.timestamp
                                    );
                                const timeFormatter = new Intl.DateTimeFormat("en-IN", {
                                    timeZone: "UTC",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                });
                                const firstTimestamp = chartPoints[0]?.timestamp;
                                const lastTimestamp = chartPoints[chartPoints.length - 1]?.timestamp;
                                const support = toNumber(preOpenChart.support);
                                const resistance = toNumber(preOpenChart.resistance);
                                const priceValues = [
                                    ...chartPoints.map((point) => point.price),
                                    support,
                                    resistance,
                                ].filter(Number.isFinite);
                                const minimumPrice = Math.min(...priceValues);
                                const maximumPrice = Math.max(...priceValues);
                                const priceRange = Math.max(maximumPrice - minimumPrice, 1);
                                const roughTickStep = priceRange / 4;
                                const tickMagnitude = 10 ** Math.floor(Math.log10(roughTickStep));
                                const normalizedTickStep = roughTickStep / tickMagnitude;
                                const niceTickMultiplier = normalizedTickStep <= 1
                                    ? 1
                                    : normalizedTickStep <= 2
                                        ? 2
                                        : normalizedTickStep <= 5
                                            ? 5
                                            : 10;
                                const yTickStep = niceTickMultiplier * tickMagnitude;
                                const roundedYAxisMinimum =
                                    Math.floor(minimumPrice / yTickStep) * yTickStep;
                                const yAxisMinimum = roundedYAxisMinimum >= minimumPrice
                                    ? roundedYAxisMinimum - yTickStep
                                    : roundedYAxisMinimum;
                                const yAxisMaximum = Math.ceil(maximumPrice / yTickStep) * yTickStep;
                                const levelDataset = (label, price, color) => ({
                                    label,
                                    data: [
                                        { x: firstTimestamp, y: price },
                                        { x: lastTimestamp, y: price },
                                    ],
                                    borderColor: color,
                                    backgroundColor: color,
                                    borderWidth: 3,
                                    borderDash: [10, 7],
                                    fill: false,
                                    pointRadius: 0,
                                    pointHoverRadius: 0,
                                    tension: 0,
                                });
                                const chartData = {
                                    datasets: [{
                                        label: "Price",
                                        data: chartPoints.map((point) => ({
                                            x: point.timestamp,
                                            y: point.price,
                                        })),
                                        borderColor: PRE_OPEN_CHART_LINE_COLOR,
                                        backgroundColor: (context) => {
                                            const { chart } = context;
                                            const { ctx, chartArea } = chart;

                                            if (!chartArea) return PRE_OPEN_CHART_FILL_COLOR;

                                            const gradient = ctx.createLinearGradient(
                                                0,
                                                chartArea.top,
                                                0,
                                                chartArea.bottom
                                            );
                                            gradient.addColorStop(0, "rgba(255, 177, 67, 0.42)");
                                            gradient.addColorStop(1, "rgba(255, 245, 226, 0.18)");
                                            return gradient;
                                        },
                                        borderWidth: 4,
                                        borderCapStyle: "round",
                                        borderJoinStyle: "round",
                                        fill: true,
                                        pointRadius: 0,
                                        pointHoverRadius: 4,
                                        tension: 0,
                                    },
                                    ...(support == null
                                        ? []
                                        : [levelDataset("Support", support, SUPPORT_LINE_COLOR)]),
                                    ...(resistance == null
                                        ? []
                                        : [levelDataset("Resistance", resistance, RESISTANCE_LINE_COLOR)]),
                                    ],
                                };
                                const chartOptions = {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    animation: false,
                                    parsing: false,
                                    normalized: true,
                                    interaction: { intersect: false, mode: "index" },
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            displayColors: false,
                                            filter: (context) => context.datasetIndex === 0,
                                            titleFont: { size: 20, weight: "bold" },
                                            bodyFont: { size: 20, weight: "bold" },
                                            padding: 10,
                                            callbacks: {
                                                title: ([context]) => context
                                                    ? `Time: ${timeFormatter.format(new Date(context.parsed.x))}`
                                                    : "",
                                                label: (context) => [
                                                    `Symbol: ${preOpenChart.data?.identifier || preOpenChart.symbol}`,
                                                    `Price: ${formatPreOpenNumber(context.parsed.y)}`,
                                                ],
                                            },
                                        },
                                    },
                                    scales: {
                                        x: {
                                            type: "linear",
                                            min: chartPoints[0]?.timestamp,
                                            max: chartPoints[chartPoints.length - 1]?.timestamp,
                                            grid: { display: false },
                                            ticks: {
                                                color: "#353535",
                                                stepSize: 2 * 60 * 1000,
                                                font: { size: 28 },
                                                callback: (value) => timeFormatter.format(new Date(value)),
                                            },
                                            afterBuildTicks: (axis) => {
                                                axis.ticks = axis.ticks.filter((tick) =>
                                                    tick.value > axis.min && tick.value < axis.max
                                                );
                                            },
                                            border: { color: "#333333", width: 2 },
                                        },
                                        y: {
                                            min: yAxisMinimum,
                                            max: yAxisMaximum === yAxisMinimum
                                                ? yAxisMinimum + yTickStep
                                                : yAxisMaximum,
                                            grid: { display: false },
                                            ticks: {
                                                color: "#312e81",
                                                stepSize: yTickStep,
                                                font: { size: 28 },
                                                padding: 20,
                                                callback: (value) => Number(value).toLocaleString("en-IN", {
                                                    useGrouping: false,
                                                    maximumFractionDigits: 2,
                                                }),
                                            },
                                            border: { display: false },
                                        },
                                    },
                                };

                                return (
                                    <div className="pod-chart-canvas-wrap">
                                        <Line data={chartData} options={chartOptions} />
                                    </div>
                                );
                            })()}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
